import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { validateImageFile, getAllowedSpecsSummary } from '@/lib/imageValidator';
import type { ProcessType } from '@/mocks/customizationMenu';
import { PROCESS_TYPE_LABELS, PROCESS_TYPE_ICONS } from '@/mocks/customizationMenu';
import {
  printingPositions,
} from '@/mocks/customizationMenu';
import type { ReplacementPart } from '@/mocks/customizationMenu';
import { finishingParts } from '@/mocks/finishingTree';
import type { ColorOption } from '@/mocks/finishingTree';
import { popularColors, premiumColors } from '@/mocks/finishingTree';
import type { ConfirmedSelection } from '@/pages/customize-quote/types';
import { nextSelectionId } from '@/pages/customize-quote/types';
import { useContainerParts } from '@/hooks/useContainerParts';
import type { ContainerPart, FinishingMethod as DbFinishingMethod } from '@/hooks/useContainerParts';
import {
  createPreviewJob,
  pollJobStatus,
  getPreviewResult,
} from '@/lib/finishingPreviewService';
import type {
  FinishingPreviewJob,
  FinishingPreviewResult,
  FinishingLine,
} from '@/mocks/finishingPreviewJob';
import {
  createPartAssemblyJob,
  pollPartAssemblyJobStatus,
} from '@/lib/partAssemblyService';
import type {
  PartAssemblyJob,
} from '@/lib/partAssemblyService';
import {
  createPrintingPreviewJob,
  pollPrintingJobStatus,
} from '@/lib/printingPreviewService';
import { downloadImage } from '@/lib/downloadImage';
import {
  getProcessResult,
  saveProcessResult,
} from '@/lib/customizeQuoteStore';


interface ProcessSectionProps {
  processId: string;
  processType: ProcessType;
  title: string;
  labelKey?: string;
  index: number;
  productId: string;
  productImage: string;
  workingImageId?: string | null;
  productName: string;
  categoryL1: string;
  categoryL2: string;
  outerDiameterMm: string;
  innerDiameterMm: string;
  heightMm: string;
  onRemove: (processId: string) => void;
  onConfirmed: (processId: string, selections: ConfirmedSelection[]) => void;
  collapseOnConfirm?: boolean;
}

// ─── Code safety helpers ───
// Ensures we never send id-format values (e.g., "part-03") instead of actual master_codes code values (e.g., "03")
function extractCode(value: string, label: string): string {
  if (!value) return '';
  if (value.startsWith('part-')) return value.slice(5);
  if (value.startsWith('method-')) return value.slice(7);
  return value;
}

// ─── Parts Replace ───
interface PartsReplaceSectionProps {
  productId: string;
  productImage: string;
  productName: string;
  outerDiameterMm: string;
  innerDiameterMm: string;
  heightMm: string;
  processId: string;
  onConfirmed: (selections: ConfirmedSelection[]) => void;
}

function PartsReplaceSection({
  productId,
  productImage,
  productName,
  outerDiameterMm,
  innerDiameterMm,
  heightMm,
  processId,
  onConfirmed,
}: PartsReplaceSectionProps) {
  const { t } = useTranslation('customize');
  const [activeTab, setActiveTab] = useState<'cap' | 'pump' | null>(null);
  const [selectedCap, setSelectedCap] = useState<ReplacementPart | null>(null);
  const [selectedPump, setSelectedPump] = useState<ReplacementPart | null>(null);
  const [lastSelectedType, setLastSelectedType] = useState<'cap' | 'pump' | null>(null);

  // ─── Resolved parts: use DB data (primary) → mock data (fallback) ───
  const [dbCapParts, setDbCapParts] = useState<ReplacementPart[]>([]);
  const [dbPumpParts, setDbPumpParts] = useState<ReplacementPart[]>([]);
  const [dbPartsLoading, setDbPartsLoading] = useState(true);

  // ─── Fetch compatible CAP/PUMP products from Supabase ───
  useEffect(() => {
    let cancelled = false;
    async function fetchCompatibleParts() {
      setDbPartsLoading(true);
      try {
        if (!supabase) {
          if (!cancelled) setDbPartsLoading(false);
          return;
        }

        // Fetch all active CAP + PUMP products with their primary working images
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('id, name, category, tags, display_price_text')
          .in('category', ['캡(CAP)', '에센스펌프'])
          .eq('status', 'active');

        if (productsError || !productsData) {
          console.error('[PartsReplace] Failed to fetch compatible products:', productsError?.message);
          if (!cancelled) setDbPartsLoading(false);
          return;
        }

        // Fetch primary working images for these products
        const productIds = productsData.map((p) => p.id);
        const { data: imagesData, error: imagesError } = await supabase
          .from('product_images')
          .select('product_id, public_url, image_role')
          .in('product_id', productIds)
          .eq('image_role', 'working');

        if (imagesError) {
          console.error('[PartsReplace] Failed to fetch images:', imagesError.message);
        }

        // Build a map: product_id → image_url (prefer working, fallback to web/original)
        const imageMap: Record<string, string> = {};
        // First pass: working images
        (imagesData || []).forEach((img) => {
          if (img.public_url && !imageMap[img.product_id]) {
            imageMap[img.product_id] = img.public_url;
          }
        });

        // Second pass: if no working image, try any image for that product
        if (Object.keys(imageMap).length < productIds.length) {
          const { data: fallbackImages } = await supabase
            .from('product_images')
            .select('product_id, public_url, image_role')
            .in('product_id', productIds)
            .eq('is_primary', true)
            .not('image_role', 'eq', 'original');
          (fallbackImages || []).forEach((img) => {
            if (img.public_url && !imageMap[img.product_id]) {
              imageMap[img.product_id] = img.public_url;
            }
          });
        }

        // Helper: extract numeric value from tags->specs safely
        const getSpecNum = (tags: Record<string, unknown>, key: string): string => {
          const specs = (tags?.specs || {}) as Record<string, unknown>;
          const val = specs[key];
          return val != null ? String(val) : '';
        };

        // Map DB rows to ReplacementPart with compatibility filtering
        const mapToPart = (row: typeof productsData[number]): ReplacementPart | null => {
          const tags = (row.tags || {}) as Record<string, unknown>;
          const outerDia = getSpecNum(tags, 'outer_diameter_mm');
          const innerDia = getSpecNum(tags, 'inner_diameter_mm');
          const material = getSpecNum(tags, 'material_outer');
          const imageUrl = imageMap[row.id] || '';

          // Compatibility check: outer_diameter_mm and inner_diameter_mm must both match
          if (outerDiameterMm && outerDia !== outerDiameterMm) return null;
          if (innerDiameterMm && innerDia !== innerDiameterMm) return null;

          const specs: Array<{ label: string; value: string }> = [];
          if (outerDia) specs.push({ label: 'outer_diameter_mm', value: outerDia });
          if (innerDia) specs.push({ label: 'inner_diameter_mm', value: innerDia });
          if (material) specs.push({ label: 'material_outer', value: material });

          return {
            id: row.id,
            name: row.name,
            type: row.category === '캡(CAP)' ? 'cap' as const : 'pump' as const,
            material: material || '',
            description: row.name,
            imageUrl,
            refColor: '#E0E0E0',
            specs,
            displayPriceText: row.display_price_text || '',
          };
        };

        const capParts: ReplacementPart[] = [];
        const pumpParts: ReplacementPart[] = [];

        productsData.forEach((row) => {
          const part = mapToPart(row);
          if (!part) return; // not compatible
          if (row.category === '캡(CAP)') {
            capParts.push(part);
          } else if (row.category === '에센스펌프') {
            pumpParts.push(part);
          }
        });

        if (!cancelled) {
          setDbCapParts(capParts);
          setDbPumpParts(pumpParts);
          setDbPartsLoading(false);
        }
      } catch (err) {
        console.error('[PartsReplace] Error fetching compatible parts:', err);
        if (!cancelled) setDbPartsLoading(false);
      }
    }
    fetchCompatibleParts();
    return () => { cancelled = true; };
  }, [productId, outerDiameterMm, innerDiameterMm]);

  // DB-only: no mock fallback — only show products that exist in Supabase
  const allDbParts: ReplacementPart[] = [...dbCapParts, ...dbPumpParts];

  // ─── AI Assembly State (multi-job: one per selected part) ───
  interface TrackedAssemblyJob {
    jobId: string;
    label: string;
    partName: string;
    partType: 'cap' | 'pump';
  }
  const [aiPrompt, setAiPrompt] = useState('');
  const [assemblyJobs, setAssemblyJobs] = useState<TrackedAssemblyJob[]>(() => {
    const saved = getProcessResult(processId);
    return saved?.imageUrl && saved.imageType === 'assembly'
      ? [{ jobId: 'restored', label: '', partName: '', partType: 'cap' as const }]
      : [];
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [jobStatuses, setJobStatuses] = useState<Record<string, PartAssemblyJob>>({});
  const [previewResults, setPreviewResults] = useState<Array<{ id: string; public_url: string | null }>>(() => {
    const saved = getProcessResult(processId);
    return saved?.imageUrl && saved.imageType === 'assembly'
      ? [{ id: 'restored', public_url: saved.imageUrl }]
      : [];
  });
  const [aiImageErrorMap, setAiImageErrorMap] = useState<Record<string, boolean>>({});
  const [elapsedDisplay, setElapsedDisplay] = useState(0);
  const [activeResultTab, setActiveResultTab] = useState<'cap' | 'pump' | null>(() => {
    const saved = getProcessResult(processId);
    return saved?.imageUrl && saved.imageType === 'assembly' ? 'cap' : null;
  });
  const [showAiPopup, setShowAiPopup] = useState(false);
  const [aiPopupMode, setAiPopupMode] = useState<'info' | 'resource'>('info');
  const [showRetryTooltip, setShowRetryTooltip] = useState(false);
  const navigate = useNavigate();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobStartTimeRef = useRef<number>(0);
  const isFirstMountRef = useRef(true);

  const scrollRef = useRef<HTMLDivElement>(null);

  const hasAiSelection = selectedCap !== null || selectedPump !== null;
  const [copied, setCopied] = useState(false);

  // ─── Build Kling API request payload JSON ───
  const handleGenerateJson = useCallback(() => {
    const elements: Array<{ image_url: string; type: string; fidelity: number }> = [];

    if (selectedCap?.imageUrl) {
      elements.push({
        image_url: selectedCap.imageUrl,
        type: 'object',
        fidelity: 0.75,
      });
    }

    if (selectedPump?.imageUrl) {
      elements.push({
        image_url: selectedPump.imageUrl,
        type: 'object',
        fidelity: 0.75,
      });
    }

    const capNames = selectedCap?.name || '';
    const pumpNames = selectedPump?.name || '';

    let autoPrompt = `${productName} 용기에 `;
    if (capNames) autoPrompt += `CAP을 ${capNames}(으)로 교체`;
    if (capNames && pumpNames) autoPrompt += `하고, `;
    if (pumpNames) autoPrompt += `PUMP를 ${pumpNames}(으)로 교체`;
    autoPrompt += '한 최종 완성 제품 이미지. 전문적인 스튜디오 조명, 깨끗한 흰색 배경, 상업용 제품 사진 품질, 고해상도.';

    const finalPrompt = aiPrompt.trim() || autoPrompt;

    const payload: Record<string, unknown> = {
      model_name: 'kling-v1-5',
      image: productImage,
      prompt: finalPrompt,
    };

    if (elements.length > 0) {
      payload.elements = elements;
    }

    const json = JSON.stringify(payload, null, 2);
    setAiPrompt(json);
    setCopied(false);
  }, [selectedCap, selectedPump, productImage, productName, aiPrompt]);

  const handleCopyJson = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(aiPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = aiPrompt;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [aiPrompt]);

  // Detect if current prompt content is JSON
  const isJsonPrompt = aiPrompt.trim().startsWith('{');

  // Reset job flow when selections change
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    setJobStatuses({});
    setGenerationError(null);
    setPreviewResults([]);
    setAiImageErrorMap({});
    setIsGenerating(false);
    setElapsedDisplay(0);
    setActiveResultTab(null);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, [selectedCap, selectedPump]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, []);

  const handleTabClick = (tab: 'cap' | 'pump') => {
    if (activeTab === tab) {
      // 같은 탭 다시 클릭 -> 선택 해제
      setActiveTab(null);
    } else {
      // 다른 탭으로 전환 -> 반대쪽 선택 초기화
      setActiveTab(tab);
      if (tab === 'cap') {
        setSelectedPump(null);
      } else {
        setSelectedCap(null);
      }
    }
  };

  const togglePart = useCallback((part: ReplacementPart) => {
    if (part.type === 'cap') {
      const isSame = selectedCap?.id === part.id;
      setSelectedCap(isSame ? null : part);
      setSelectedPump(null);
      if (!isSame) setLastSelectedType('cap');
    } else {
      const isSame = selectedPump?.id === part.id;
      setSelectedPump(isSame ? null : part);
      setSelectedCap(null);
      if (!isSame) setLastSelectedType('pump');
    }
  }, [selectedCap, selectedPump]);

  const toggleCapItem = (part: ReplacementPart) => {
    togglePart(part);
  };

  const togglePumpItem = (part: ReplacementPart) => {
    togglePart(part);
  };

  const handleConfirm = () => {
    const selections: ConfirmedSelection[] = [];
    if (selectedCap) {
      selections.push({
        id: nextSelectionId(),
        partId: 'part-cap',
        partCode: '01',
        partName: 'CAP',
        methodId: `rep-${selectedCap.id}`,
        methodCode: `rep-${selectedCap.id}`,
        methodName: selectedCap.name,
        colorHex: selectedCap.refColor || '#E0E0E0',
        colorName: selectedCap.material,
      });
    }
    if (selectedPump) {
      selections.push({
        id: nextSelectionId(),
        partId: 'part-pump',
        partCode: '07',
        partName: 'PUMP',
        methodId: `rep-${selectedPump.id}`,
        methodCode: `rep-${selectedPump.id}`,
        methodName: selectedPump.name,
        colorHex: selectedPump.refColor || '#E0E0E0',
        colorName: selectedPump.material,
      });
    }
    onConfirmed(selections);
  };

  // ─── AI Preview Generation (part_assembly_jobs pipeline) ───
  const handleGeneratePreview = async () => {
    if (!hasAiSelection) return;

    const jobsToCreate: { label: string; partName: string; partType: 'cap' | 'pump'; partImageUrl: string }[] = [];
    if (selectedCap?.imageUrl) {
      jobsToCreate.push({ label: 'CAP', partName: selectedCap.name, partType: 'cap', partImageUrl: selectedCap.imageUrl });
    }
    if (selectedPump?.imageUrl) {
      jobsToCreate.push({ label: 'PUMP', partName: selectedPump.name, partType: 'pump', partImageUrl: selectedPump.imageUrl });
    }
    if (jobsToCreate.length === 0) return;

    setIsGenerating(true);
    setGenerationError(null);
    setPreviewResults([]);
    setAiImageErrorMap({});
    setJobStatuses({});
    setAssemblyJobs([]);
    setActiveResultTab(null);
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }

    try {
      const created: TrackedAssemblyJob[] = [];
      for (const jobDef of jobsToCreate) {
        const job = await createPartAssemblyJob({
          product_id: productId,
          base_image_url: productImage,
          part_image_url: jobDef.partImageUrl,
          source: 'readdy_customer_ui',
          request_locale: 'ko-KR',
        });
        created.push({
          jobId: job.id,
          label: jobDef.label,
          partName: jobDef.partName,
          partType: jobDef.partType,
        });
        setJobStatuses((prev) => ({ ...prev, [job.id]: job }));
      }
      setAssemblyJobs(created);
      jobStartTimeRef.current = Date.now();
      setElapsedDisplay(0);

      elapsedTimerRef.current = setInterval(() => {
        setElapsedDisplay(Math.floor((Date.now() - jobStartTimeRef.current) / 1000));
      }, 1000);

      const allJobIds = created.map((j) => j.jobId);
      const jobTypeMap: Record<string, 'cap' | 'pump'> = {};
      for (const j of created) { jobTypeMap[j.jobId] = j.partType; }

      pollTimerRef.current = setInterval(async () => {
        try {
          const newStatuses: Record<string, PartAssemblyJob> = {};
          const newResults: Array<{ id: string; public_url: string | null }> = [];
          let completedCount = 0;
          let failedCount = 0;
          let lastError: string | null = null;

          for (const jobId of allJobIds) {
            const status = await pollPartAssemblyJobStatus(jobId);
            newStatuses[jobId] = status;

            if (status.status === 'completed' && status.result_image_url) {
              completedCount += 1;
              newResults.push({
                id: status.result_id || status.id,
                public_url: status.result_image_url,
              });
            } else if (status.status === 'failed') {
              failedCount += 1;
              lastError = status.error_message || 'Assembly generation failed.';
            }
          }

          setJobStatuses((prev) => ({ ...prev, ...newStatuses }));

          if (newResults.length > 0) {
            setPreviewResults((prev) => {
              const existingIds = new Set(prev.map((r) => r.id));
              const uniqueNew = newResults.filter((r) => !existingIds.has(r.id));
              return [...prev, ...uniqueNew];
            });
          }

          const totalJobs = allJobIds.length;
          if (completedCount + failedCount >= totalJobs) {
            if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
            if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
            setIsGenerating(false);

            if (failedCount > 0 && completedCount === 0) {
              setGenerationError(lastError);
            } else if (completedCount > 0) {
              // Set active tab to first completed result's type
              const firstCompletedType = created.find((j) => {
                const s = newStatuses[j.jobId];
                return s?.status === 'completed' && s.result_id;
              })?.partType || created[0]?.partType || 'cap';
              setActiveResultTab(firstCompletedType);

              // Persist the first result so it survives the image-viewer round-trip.
              if (newResults.length > 0) {
                saveProcessResult(processId, {
                  imageUrl: newResults[0].public_url || '',
                  title: t('customize.parts.assemblyResult'),
                  imageType: 'assembly',
                  selections: [],
                });
              }
            }
          }
        } catch (err) {
          console.error('[PartsReplace] Poll iteration error:', err);
        }
      }, 2000);
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to create assembly job.');
      setIsGenerating(false);
      if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    }
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setJobStatuses({});
    setPreviewResults([]);
    handleGeneratePreview();
  };

  const handleCancelGeneration = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setIsGenerating(false);
    setJobStatuses({});
    setPreviewResults([]);
    setGenerationError(null);
    setElapsedDisplay(0);
  };

  const hasSelection = selectedCap !== null || selectedPump !== null;

  return (
    <div className="space-y-4">

      {/* ─── Row 1: Large Product Image (Left) + Thumbnail Box (Middle) + Controls (Right) ─── */}
      <div className="flex items-start gap-6">
        {/* Product image area — main image only */}
        <div className="shrink-0">
          {/* Main product image */}
          <div className="w-[307px] h-[307px] bg-background-100 rounded-xl border border-ink-100 flex items-center justify-center p-4 overflow-hidden">
            <img
              src={productImage}
              alt={productName}
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* ─── Middle: Selected Part Thumbnail Box ─── */}
        <div className="shrink-0">
          <div className="rounded-xl border border-ink-100 bg-background-50 overflow-hidden flex flex-col">
            <div className="w-[100px] h-[100px] flex items-center justify-center p-2 gap-2">
              {(selectedCap || selectedPump) ? (() => {
                const displayType: 'cap' | 'pump' | null =
                  (lastSelectedType === 'cap' && selectedCap) ? 'cap' :
                  (lastSelectedType === 'pump' && selectedPump) ? 'pump' :
                  selectedCap ? 'cap' :
                  selectedPump ? 'pump' :
                  null;
                const displayPart = displayType === 'cap' ? selectedCap : selectedPump;
                if (!displayPart) return null;
                return (
                  <div className="w-full h-full bg-background-100 rounded-lg border border-ink-100 flex items-center justify-center p-2 overflow-hidden shrink-0">
                    {displayPart.imageUrl ? (
                      <img
                        src={displayPart.imageUrl}
                        alt={displayPart.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-xl border-2 border-white"
                        style={{ backgroundColor: displayPart.refColor || '#e0e0e0' }}
                      />
                    )}
                  </div>
                );
              })() : (
                <div className="flex flex-col items-center gap-2 text-center p-4">
                  <div className="w-8 h-8 rounded-full bg-ink-50 flex items-center justify-center">
                    <i className="ri-image-add-line text-ink-300 text-base" />
                  </div>
                  <p className="text-[10px] text-ink-400 leading-relaxed">
                    {t('customize.parts.selectHint')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controls — right side: always shows parts grid */}
        <div className="flex-1 min-w-0 flex flex-col" style={{ height: 307 }}>
          <p className="text-base font-extrabold text-ink-700 mb-1">{productName}</p>
          <p className="text-sm text-ink-500 mb-2 font-bold">
            토출구(π) Φ{outerDiameterMm || '--'}mm{heightMm ? ` · 캡높이 ${heightMm}mm` : ''}
          </p>
          <p className="text-[11px] text-ink-500 mb-4">{t('customize.parts.selectPart')}</p>
          <div className="flex items-center gap-3 mb-4">
            {/* CAP radio button — PUMP이 선택된 상태가 아닐 때만 표시 */}
            {activeTab !== 'pump' && (
              <button
                onClick={() => handleTabClick('cap')}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'cap'
                    ? 'bg-accent-50 border-accent-400 text-accent-700'
                    : 'bg-white border-ink-200 text-ink-600 hover:border-accent-300 hover:text-accent-600'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  activeTab === 'cap' ? 'bg-accent-500 text-white' : 'border-2 border-ink-300'
                }`}>
                  {activeTab === 'cap' && <i className="ri-check-line text-[10px]" />}
                </span>
                <i className="ri-capsule-line" />
                <span className={activeTab === 'cap' ? 'text-accent-700' : ''}>CAP</span>
                {selectedCap && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                    activeTab === 'cap' ? 'bg-accent-200 text-accent-700' : 'bg-accent-100 text-accent-700'
                  }`}>
                    ✓
                  </span>
                )}
              </button>
            )}

            {/* PUMP radio button — CAP이 선택된 상태가 아닐 때만 표시 */}
            {activeTab !== 'cap' && (
              <button
                onClick={() => handleTabClick('pump')}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'pump'
                    ? 'bg-secondary-50 border-secondary-400 text-secondary-700'
                    : 'bg-white border-ink-200 text-ink-600 hover:border-secondary-300 hover:text-secondary-600'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  activeTab === 'pump' ? 'bg-secondary-500 text-white' : 'border-2 border-ink-300'
                }`}>
                  {activeTab === 'pump' && <i className="ri-check-line text-[10px]" />}
                </span>
                <i className="ri-contrast-drop-line" />
                <span className={activeTab === 'pump' ? 'text-secondary-700' : ''}>PUMP</span>
                {selectedPump && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                    activeTab === 'pump' ? 'bg-secondary-200 text-secondary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    ✓
                  </span>
                )}
              </button>
            )}
          </div>

          {/* ─── Parts Grid — always visible ─── */}
          <div className="rounded-xl border border-ink-100 bg-background-50 overflow-hidden flex-1 min-h-0 flex flex-col relative">
            {dbPartsLoading ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2 flex-1">
                <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-500 rounded-full animate-spin" />
                <p className="text-[11px] text-ink-400 font-medium">{t('customize.parts.loading')}</p>
              </div>
            ) : allDbParts.length > 0 ? (
              <>
                {/* Left scroll button */}
                <button
                  onClick={() => { if (scrollRef.current) scrollRef.current.scrollBy({ left: -120, behavior: 'smooth' }); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/90 border border-ink-200 shadow-sm flex items-center justify-center hover:bg-white cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-ink-500 text-sm" />
                </button>
                <div
                  ref={scrollRef}
                  className="px-10 py-3 flex flex-nowrap items-center justify-start gap-2 flex-1 overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-mandatory"
                >
                  {(activeTab ? allDbParts.filter((p) => p.type === activeTab) : allDbParts).map((part) => {
                    const isSelected = (part.type === 'cap' && selectedCap?.id === part.id) || (part.type === 'pump' && selectedPump?.id === part.id);
                    const borderCls = part.type === 'cap'
                      ? (isSelected ? 'border-accent-400 ring-2 ring-accent-200' : 'border-ink-100 hover:border-accent-200')
                      : (isSelected ? 'border-secondary-400 ring-2 ring-secondary-200' : 'border-ink-100 hover:border-secondary-200');
                    return (
                      <div
                        key={part.id}
                        className="flex flex-col items-center shrink-0 snap-center"
                      >
                        <button
                          onClick={() => togglePart(part)}
                          className={`relative flex items-center justify-center h-[110px] w-[110px] p-1.5 rounded-lg border-2 transition-all cursor-pointer bg-white ${borderCls}`}
                        >
                          <div className="w-full h-full bg-background-100 rounded-md flex items-center justify-center p-1 overflow-hidden">
                            {part.imageUrl ? (
                              <img src={part.imageUrl} alt={part.name} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-10 h-10 rounded-full" style={{ backgroundColor: part.refColor || '#e0e0e0' }} />
                            )}
                          </div>
                          {isSelected && (
                            <div className={`absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center ${part.type === 'cap' ? 'bg-accent-500' : 'bg-secondary-500'}`}>
                              <i className="ri-check-line text-white text-[9px]" />
                            </div>
                          )}
                        </button>
                        {part.displayPriceText && (
                          <span className="text-[9px] font-bold text-ink-500 leading-none mt-1.5 truncate max-w-[110px] text-center whitespace-nowrap">{part.displayPriceText}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Right scroll button */}
                <button
                  onClick={() => { if (scrollRef.current) scrollRef.current.scrollBy({ left: 120, behavior: 'smooth' }); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/90 border border-ink-200 shadow-sm flex items-center justify-center hover:bg-white cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-ink-500 text-sm" />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 gap-2 flex-1">
                <i className="ri-emotion-sad-line text-ink-300 text-xl" />
                <p className="text-[11px] text-ink-400 font-medium">{t('customize.parts.empty')}</p>
              </div>
            )}
          </div>
        </div>
      </div>



      {/* ─── {t('customize.confirm')} ─── */}
      {hasSelection && (
        <div className="flex items-center justify-center gap-2 pt-1">
          <button
            onClick={handleConfirm}
            className="px-6 py-2.5 text-sm font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-check-line" />
            {t('customize.confirm')}
          </button>
        </div>
      )}



      {/* ─── AI Preview Strip (multi-job assembly) ─── */}
      <div className="space-y-3">
        {/* Idle: Generate button */}
        {!isGenerating && previewResults.length === 0 && !generationError && (
          <div key="pr-idle" className="flex justify-center">
            <button
              onClick={() => { setAiPopupMode('info'); setShowAiPopup(true); }}
              disabled={!hasAiSelection}
              className={`px-5 py-2.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                hasAiSelection
                  ? 'bg-warm-500 text-white hover:bg-warm-600'
                  : 'bg-ink-100 text-ink-400 cursor-not-allowed'
              }`}
            >
              <i className="ri-sparkling-line" />
              {t('customize.parts.aiPreview')}
            </button>
          </div>
        )}

        {/* ─── 통합 AI 안내 팝업 ─── */}
        {showAiPopup && (
          <div key="pr-popup" className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setShowAiPopup(false)}
            />
            <div className="relative z-10 mx-4 w-full max-w-[440px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
              {/* 상단 헤더 */}
              <div className={`border-b px-6 py-4 flex items-center gap-3 ${
                aiPopupMode === 'info' ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'
              }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                  aiPopupMode === 'info' ? 'bg-amber-100' : 'bg-rose-100'
                }`}>
                  <i className={`text-xl ${
                    aiPopupMode === 'info' ? 'ri-robot-2-line text-amber-500' : 'ri-emotion-sad-line text-rose-500'
                  }`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-extrabold ${
                    aiPopupMode === 'info' ? 'text-amber-700' : 'text-rose-700'
                  }`}>
                    {aiPopupMode === 'info' ? t('customize.parts.aiPreview') : t('customize.parts.aiUnavailable')}
                  </p>
                  <p className={`text-[10px] mt-0.5 ${
                    aiPopupMode === 'info' ? 'text-amber-500' : 'text-rose-500'
                  }`}>
                    {aiPopupMode === 'info' ? t('customize.parts.confirmBeforeStart') : t('customize.parts.currentlyUnavailable')}
                  </p>
                </div>
              </div>

              {/* 본문 - 개발단계: 두 메시지 모두 표시 */}
              <div className="px-6 py-5 space-y-3">
                {/* AI 안내 메시지 */}
                <div className={`rounded-xl p-4 border ${
                  aiPopupMode === 'info'
                    ? 'bg-amber-50/60 border-amber-200 ring-2 ring-amber-200'
                    : 'bg-amber-50/30 border-amber-100'
                }`}>
                  <div className="flex items-start gap-2.5">
                    <i className={`text-base shrink-0 mt-0.5 ${
                      aiPopupMode === 'info' ? 'ri-information-line text-amber-400' : 'ri-information-line text-amber-300'
                    }`} />
                    <p className="text-sm font-bold text-ink-700 leading-relaxed">
                      {t('customize.parts.aiNotice')}
                    </p>
                  </div>
                </div>

                {/* 리소스 부족 메시지 */}
                <div className={`rounded-xl p-4 border ${
                  aiPopupMode === 'resource'
                    ? 'bg-rose-50/60 border-rose-200 ring-2 ring-rose-200'
                    : 'bg-rose-50/30 border-rose-100'
                }`}>
                  <div className="flex items-start gap-2.5">
                    <i className={`text-base shrink-0 mt-0.5 ${
                      aiPopupMode === 'resource' ? 'ri-error-warning-line text-rose-400' : 'ri-error-warning-line text-rose-300'
                    }`} />
                    <p className="text-sm font-bold text-ink-700 leading-relaxed">
                      {t('customize.parts.resourceNotice')}
                    </p>
                  </div>
                </div>

                {/* 모드 전환 토글 (개발단계용) */}
                <div className="flex items-center justify-center gap-2 pt-1">
                  <span className="text-[10px] text-ink-400">팝업 모드:</span>
                  <button
                    onClick={() => setAiPopupMode('info')}
                    className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      aiPopupMode === 'info'
                        ? 'bg-amber-500 text-white'
                        : 'bg-ink-100 text-ink-500 hover:bg-amber-100'
                    }`}
                  >
                    안내
                  </button>
                  <button
                    onClick={() => setAiPopupMode('resource')}
                    className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      aiPopupMode === 'resource'
                        ? 'bg-rose-500 text-white'
                        : 'bg-ink-100 text-ink-500 hover:bg-rose-100'
                    }`}
                  >
                    리소스 부족
                  </button>
                </div>
              </div>

              {/* 하단 버튼 */}
              <div className="px-6 pb-5 pt-2 flex items-center gap-3">
                <button
                  onClick={() => setShowAiPopup(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold border-2 border-ink-200 text-ink-600 hover:bg-ink-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('customize.cancel')}
                </button>
                {aiPopupMode === 'info' ? (
                  <button
                    onClick={() => { setShowAiPopup(false); handleGeneratePreview(); }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-warm-500 text-white hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-sparkling-line" />
                    {t('customize.parts.start')}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAiPopup(false)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-ink-100 text-ink-600 hover:bg-ink-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('customize.parts.confirm')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Multi-job progress panel */}
        {isGenerating && assemblyJobs.length > 0 && (
          <div key="pr-progress" className="bg-gray-200 rounded-xl px-5 py-4 space-y-2.5">
            {assemblyJobs.map((aj) => {
              const js = jobStatuses[aj.jobId];
              const pct = js?.progress_percent || 0;
              return (
                <div key={aj.jobId} className="flex items-center gap-3">
                  <p className="text-[10px] font-bold text-gray-700 shrink-0 whitespace-nowrap">{t('customize.progressLabel')}</p>
                  <div className="flex-1 h-1.5 bg-gray-300 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-700 font-mono shrink-0">{Math.floor(elapsedDisplay / 60)}:{String(elapsedDisplay % 60).padStart(2, '0')}</span>
                  <span className="text-[10px] text-gray-700 font-mono w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Results with tab switcher */}
        {previewResults.length > 0 && !isGenerating && (
          <div key="pr-result" className="bg-blue-50/60 border border-blue-200 rounded-xl px-5 py-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <i className="ri-check-double-line text-blue-500 text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-extrabold text-blue-700">{t('customize.aiGeneratedImage')}</p>
                <p className="text-[10px] text-blue-500 mt-0.5">
                  {t('customize.parts.resultCount', { count: previewResults.length })}
                </p>
              </div>
              <button
                onClick={() => { setPreviewResults([]); setJobStatuses({}); }}
                className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center cursor-pointer text-blue-400 hover:text-blue-600 transition-colors"
                title={t('customize.close')}
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Tab switcher (only show when multiple results) */}
            {assemblyJobs.length > 1 && (
              <div className="flex items-center gap-1 bg-blue-100/50 rounded-lg p-0.5">
                {assemblyJobs.map((aj) => {
                  const hasResult = previewResults.some((r) => {
                    const js = jobStatuses[aj.jobId];
                    return js?.result_id === r.id;
                  });
                  return (
                    <button
                      key={aj.jobId}
                      onClick={() => setActiveResultTab(aj.partType)}
                      className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                        activeResultTab === aj.partType
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-blue-500 hover:text-blue-600'
                      }`}
                    >
                      {aj.label}
                      {!hasResult && <span className="ml-1 text-[8px] text-rose-400">{t('customize.parts.failed')}</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Active result image */}
            {(() => {
              const activeJob = assemblyJobs.find((j) => j.partType === activeResultTab) || assemblyJobs[0];
              const activeJobStatus = activeJob ? jobStatuses[activeJob.jobId] : null;
              const activeResult = activeJobStatus?.result_id
                ? previewResults.find((r) => r.id === activeJobStatus.result_id)
                : previewResults[0];
              const displayUrl = activeResult?.public_url || '';
              const resultId = activeResult?.id || '';
              const hasError = aiImageErrorMap[resultId];

              return (
                <>
                <div className="flex justify-start items-start gap-[100px]">
                <div className="w-[292px] h-[292px] rounded-lg overflow-hidden border border-blue-200 bg-background-100 flex items-center justify-center shrink-0 relative group">
                  {(!displayUrl || hasError) ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center min-w-[200px]">
                      <i className="ri-image-line text-3xl text-ink-300" />
                      <p className="text-xs text-ink-500">{t('customize.imageLoadError')}</p>
                      <button
                        onClick={() => {
                          setAiImageErrorMap((prev) => {
                            const next = { ...prev };
                            delete next[resultId];
                            return next;
                          });
                          handleRetryGeneration();
                        }}
                        className="text-[10px] text-warm-500 underline cursor-pointer"
                      >
                        {t('customize.regenerate')}
                      </button>
                    </div>
                  ) : (
                    <>
                    <img
                      src={displayUrl}
                      alt={`${activeJob?.label || ''} ${t('customize.parts.assemblyResult')}`}
                      onError={() => {
                        if (resultId) {
                          setAiImageErrorMap((prev) => ({ ...prev, [resultId]: true }));
                        }
                      }}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => navigate('/image-viewer', { state: { imageUrl: displayUrl, title: `${activeJob?.label || ''} ${t('customize.parts.assemblyResult')}`, imageType: 'assembly' } })}
                        className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                        title={t('customize.viewLarge')}
                      >
                        <i className="ri-zoom-in-line text-sm" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadImage(displayUrl, `${activeJob?.label || ''} ${t('customize.parts.assemblyResult')}.png`); }}
                        className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                        title={t('customize.download')}
                      >
                        <i className="ri-download-line text-sm" />
                      </button>
                    </div>
                    </>
                  )}
                </div>
                <div className="w-[300px] text-xs leading-relaxed space-y-3 shrink-0">
                  <p className="text-sm font-extrabold text-ink-700">{t('customize.aiImageInfo')}</p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo1')}
                  </p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo2')}
                  </p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo3')}
                  </p>
                </div>
              </div>
              <div className="w-[292px] flex items-center justify-center relative">
                <button
                  onClick={() => setShowRetryTooltip(!showRetryTooltip)}
                  className="px-3 py-1.5 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-refresh-line" />
                  {t('customize.aiGenerateAgain')}
                </button>
                {showRetryTooltip && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-20 bg-ink-700 text-white rounded-lg px-3 py-2 shadow-lg max-w-[280px] animate-in fade-in slide-in-from-left-2 duration-150">
                    <p className="text-[9px] leading-relaxed whitespace-normal">
                      i2i에 이전 결과 버퍼 + 새 프롬프트를 넣는 방식은 기술적으로 가능합니다. parent_result_id(또는 result 경로)를 입력으로 받는 refine job을 새로 두고, 그 이미지를 working으로 generateImageWithKling에 넘김
                    </p>
                    <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-r-[6px] border-r-ink-700 border-b-[5px] border-b-transparent" />
                  </div>
                )}
              </div>
              </>
              );
            })()}

          </div>
        )}

        {/* Error state */}
        {generationError && !isGenerating && previewResults.length === 0 && (
          <div key="pr-error" className="bg-rose-50/60 border border-rose-200 rounded-xl px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                <i className="ri-error-warning-line text-rose-500 text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-extrabold text-rose-600">{t('customize.generateFailed')}</p>
                <p className="text-[10px] text-rose-500 mt-0.5">{generationError}</p>
                <button
                  onClick={handleRetryGeneration}
                  className="mt-2 px-4 py-1.5 text-[10px] font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-refresh-line" />
                  {t('customize.retry')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>


    </div>
  );
}

// ─── Color Only ───
function ColorOnlySection({
  onConfirmed,
}: {
  onConfirmed: (selections: ConfirmedSelection[]) => void;
}) {
  const { t } = useTranslation('customize');
  const [colorTab, setColorTab] = useState<'popular' | 'premium' | 'custom'>('popular');
  const [pickedColor, setPickedColor] = useState<ColorOption | null>(null);
  const [customPantone, setCustomPantone] = useState('');
  const [customHex, setCustomHex] = useState('#000000');

  const popularByCategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    popularColors.forEach((c) => {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    });
    return map;
  }, []);

  const premiumBySubcategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    premiumColors.forEach((c) => {
      const key = c.subcategory || c.category;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, []);

  const handleConfirm = () => {
    if (!pickedColor) return;
    onConfirmed([{
      id: nextSelectionId(),
      partId: 'part-body',
      partCode: '03',
      partName: 'Body',
      methodId: 'method-color-change',
      methodCode: '07',
      methodName: t('customize.changeColor'),
      colorHex: pickedColor.hex,
      colorName: pickedColor.pantoneCode || pickedColor.name,
    }]);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-400">{t('customize.color.selectHint')}</p>

      {/* Color Selector */}
      <div>
        <div className="flex items-center bg-ink-50 rounded-lg p-0.5 mb-3">
          {(['popular', 'premium', 'custom'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setColorTab(tab)}
              className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                colorTab === tab ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
              }`}
            >
              {tab === 'popular' ? t('customize.popularColors') : tab === 'premium' ? 'Premium' : t('customize.customInput')}
            </button>
          ))}
        </div>

        {colorTab === 'popular' && (
          <div className="space-y-3 max-h-[200px] overflow-y-auto">
            {Object.entries(popularByCategory).map(([cat, colors]) => (
              <div key={cat}>
                <p className="text-[10px] font-bold text-ink-400 uppercase mb-1.5">{cat}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                  {colors.map((c) => (
                    <button
                      key={`pop-${c.pantoneCode}-${c.hex}`}
                      onClick={() => setPickedColor(c)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all cursor-pointer ${
                        pickedColor?.hex === c.hex
                          ? 'bg-warm-50 ring-2 ring-warm-400'
                          : 'hover:bg-cream-50 border border-transparent hover:border-ink-100'
                      }`}
                    >
                      <div
                        className="w-8 h-8 rounded-full border border-ink-200"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="text-[9px] text-ink-500 font-medium truncate w-full text-center">
                        {c.pantoneCode || c.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {colorTab === 'premium' && (
          <div className="space-y-3 max-h-[200px] overflow-y-auto">
            {Object.entries(premiumBySubcategory).map(([sub, colors]) => (
              <div key={sub}>
                <p className="text-[10px] font-bold text-ink-400 uppercase mb-1.5">{sub}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                  {colors.map((c) => (
                    <button
                      key={`prem-${c.pantoneCode}-${c.hex}`}
                      onClick={() => setPickedColor(c)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all cursor-pointer ${
                        pickedColor?.hex === c.hex
                          ? 'bg-warm-50 ring-2 ring-warm-400'
                          : 'hover:bg-cream-50 border border-transparent hover:border-ink-100'
                      }`}
                    >
                      <div
                        className="w-8 h-8 rounded-full border border-ink-200"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="text-[9px] text-ink-500 font-medium truncate w-full text-center">
                        {c.pantoneCode || c.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {colorTab === 'custom' && (
          <div className="bg-cream-50/70 rounded-lg p-3 border border-ink-100 space-y-2">
            <div>
              <label className="text-[10px] font-bold text-ink-600 block mb-1">{t('customize.pantoneCode')}</label>
              <input
                type="text"
                value={customPantone}
                onChange={(e) => setCustomPantone(e.target.value)}
                placeholder={t('customize.colorPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customHex}
                onChange={(e) => setCustomHex(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-ink-200 p-0.5"
              />
              <input
                type="text"
                value={customHex}
                onChange={(e) => setCustomHex(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-ink-200 rounded-lg font-mono bg-white"
              />
            </div>
            <button
              onClick={() => setPickedColor({ name: 'Custom', hex: customHex, category: 'Custom', pantoneCode: customPantone || undefined })}
              className="w-full py-2 text-xs font-bold bg-warm-500 text-white rounded-lg hover:bg-warm-600 cursor-pointer whitespace-nowrap"
            >
              {t('customize.selectColorButton')}
            </button>
          </div>
        )}
      </div>

      {/* Selected Preview */}
      {pickedColor && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <div className="w-10 h-10 rounded-lg border-2 border-white shadow-sm" style={{ backgroundColor: pickedColor.hex }} />
          <div>
            <p className="text-sm font-bold text-emerald-700">{pickedColor.pantoneCode || pickedColor.name}</p>
            <p className="text-[10px] text-emerald-500 font-mono">{pickedColor.hex}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleConfirm}
        disabled={!pickedColor}
        className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
          pickedColor
            ? 'bg-warm-500 text-white hover:bg-warm-600'
            : 'bg-ink-100 text-ink-400 cursor-not-allowed'
        }`}
      >
        <i className="ri-check-line" />
        {t('customize.confirm')}
      </button>
    </div>
  );
}

// ─── Finishing Color (Full 3-step) ───
function FinishingColorSection({
  productId,
  productImage,
  workingImageId,
  productName,
  categoryL1,
  categoryL2,
  processId,
  onConfirmed,
}: {
  productId: string;
  productImage: string;
  workingImageId?: string | null;
  productName: string;
  categoryL1: string;
  categoryL2: string;
  processId: string;
  onConfirmed: (selections: ConfirmedSelection[]) => void;
}) {
  const { t } = useTranslation('customize');
  const { parts: dbParts, loading: partsLoading } = useContainerParts(categoryL1, categoryL2);

  // Use DB parts if available, fallback to mock
  const partsToUse = useMemo(() => {
    if (dbParts.length > 0) return dbParts;
    return finishingParts;
  }, [dbParts]);
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [lastPickedColor, setLastPickedColor] = useState<ColorOption | null>(null);
  const [colorTab, setColorTab] = useState<'popular' | 'premium' | 'custom'>('popular');
  const [customPantoneCode, setCustomPantoneCode] = useState('');
  const [customPantoneHex, setCustomPantoneHex] = useState('#000000');
  const [customColorError, setCustomColorError] = useState<string | null>(null);
  const [confirmedSelections, setConfirmedSelections] = useState<ConfirmedSelection[]>(() => {
    const saved = getProcessResult(processId);
    return saved?.imageUrl && saved.imageType === 'finishing' ? saved.selections || [] : [];
  });

  // ─── Dedicated Working/Preview Image Area ───
  const [displayImageUrl, setDisplayImageUrl] = useState<string>(productImage);
  const [displayImageLabel, setDisplayImageLabel] = useState<string>(t('customize.finishing.workingImage'));
  const [displayedImageSize, setDisplayedImageSize] = useState<{ w: number; h: number } | null>(null);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDisplayedImageSize({ w: img.naturalWidth, h: img.naturalHeight });
  };

  // ─── AI Generation State ───
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<FinishingPreviewJob | null>(null);
  const [previewResult, setPreviewResult] = useState<FinishingPreviewResult | null>(() => {
    const saved = getProcessResult(processId);
    if (saved?.imageUrl && saved.imageType === 'finishing') {
      return {
        id: 'restored',
        job_id: '',
        product_id: productId,
        working_image_id: workingImageId || '',
        image_url: saved.imageUrl,
        result_status: 'completed',
        width: 1024,
        height: 1024,
        format: 'png',
      };
    }
    return null;
  });
  const [aiImageError, setAiImageError] = useState(false);
  const [elapsedDisplay, setElapsedDisplay] = useState(0);
  const [showRetryTooltip, setShowRetryTooltip] = useState(false);
  const navigate = useNavigate();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobStartTimeRef = useRef<number>(0);
  const isFirstMountRef = useRef(true);
  const previewImageContainerRef = useRef<HTMLDivElement>(null);

  const selectedPart = useMemo(() => partsToUse.find((p: ContainerPart) => p.id === selectedPartId) || null, [selectedPartId, partsToUse]);
  const selectedMethod = useMemo(() => selectedPart?.methods.find((m: DbFinishingMethod) => m.id === selectedMethodId) || null, [selectedPart, selectedMethodId]);
  const confirmedPartIds = useMemo(() => new Set(confirmedSelections.map((s) => s.partId)), [confirmedSelections]);

  const popularByCategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    popularColors.forEach((c) => {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    });
    return map;
  }, []);

  const premiumBySubcategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    premiumColors.forEach((c) => {
      const key = c.subcategory || c.category;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, []);

  const premiumSubLabels: Record<string, string> = {
    metallic: t('customize.metallic'),
    pearl: t('customize.pearl'),
    'soft-touch': t('customize.softTouch'),
  };

  const handleConfirmCustomColor = () => {
    const trimmedCode = customPantoneCode.trim();
    if (!trimmedCode) {
      setCustomColorError(t('customize.pantoneRequired'));
      return false;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(customPantoneHex)) {
      setCustomColorError(t('customize.hexInvalid'));
      return false;
    }
    setCustomColorError(null);
    setLastPickedColor({
      name: 'Custom',
      hex: customPantoneHex,
      category: 'Custom',
      pantoneCode: trimmedCode,
    });
    return true;
  };

  // Reset job flow when confirmed selections change
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    setJobStatus(null);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setIsGenerating(false);
    setElapsedDisplay(0);
    setDisplayedImageSize(null);
    setDisplayImageUrl(productImage);
    setDisplayImageLabel(t('customize.finishing.workingImage'));
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, [confirmedSelections]);

  // ─── When previewResult arrives, update dedicated image area ───
  useEffect(() => {
    if (previewResult && !isGenerating) {
      setDisplayedImageSize(null);
      setDisplayImageUrl(previewResult.image_url);
      setDisplayImageLabel(t('customize.finishing.aiResult'));
    }
  }, [previewResult, isGenerating]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, []);

  const handleGeneratePreview = async () => {
    if (confirmedSelections.length === 0) return;

    // Batch ALL selections into one structured_lines array — single request, single Kling job
    const structuredLines: FinishingLine[] = confirmedSelections.map((s) => ({
      part_code: extractCode(s.partCode, s.partId),
      finish_code: extractCode(s.methodCode, s.methodId),
      color_code: s.colorHex,
    }));

    setIsGenerating(true);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setJobStatus(null);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    try {
      const job = await createPreviewJob({
        product_id: productId,
        working_image_id: workingImageId || productImage,
        structured_lines: structuredLines,
        source: 'readdy_customer_ui',
        request_locale: 'ko-KR',
      });

      setJobStatus(job);
      jobStartTimeRef.current = Date.now();
      setElapsedDisplay(0);

      elapsedTimerRef.current = setInterval(() => {
        setElapsedDisplay(Math.floor((Date.now() - jobStartTimeRef.current) / 1000));
      }, 1000);

      const jobId = job.id;
      pollTimerRef.current = setInterval(async () => {
        try {
          const status = await pollJobStatus(jobId);
          setJobStatus(status);

          if (status.status === 'completed' && status.result_id) {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }

            try {
              const result = await getPreviewResult(status.result_id);
              setPreviewResult(result);
              saveProcessResult(processId, {
                imageUrl: result.image_url,
                title: t('customize.finishing.resultTitle'),
                imageType: 'finishing',
                selections: confirmedSelections,
              });
              setIsGenerating(false);
            } catch (err) {
              setGenerationError(err instanceof Error ? err.message : 'Failed to fetch preview result.');
              setIsGenerating(false);
            }
          }

          if (status.status === 'failed') {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }
            setGenerationError(status.error_message || 'AI preview generation failed.');
            setIsGenerating(false);
          }
        } catch (err) {
          console.error('[FinishingColor] Poll iteration error:', err);
        }
      }, 2000);
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to create preview job.');
      setIsGenerating(false);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setJobStatus(null);
    setPreviewResult(null);
    handleGeneratePreview();
  };

  const handleCancelGeneration = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setIsGenerating(false);
    setJobStatus(null);
    setPreviewResult(null);
    setGenerationError(null);
    setElapsedDisplay(0);
  };

  const handleSelectPart = (partId: string) => {
    if (selectedPartId === partId) {
      setSelectedPartId(null);
      setSelectedMethodId(null);
      setLastPickedColor(null);
    } else {
      setSelectedPartId(partId);
      setSelectedMethodId(null);
      setLastPickedColor(null);
    }
  };

  const handleSelectMethod = (methodId: string) => {
    if (methodId === selectedMethodId) {
      setSelectedMethodId(null);
      setLastPickedColor(null);
    } else {
      setSelectedMethodId(methodId);
      setLastPickedColor(null);
    }
  };

  const handleSelectColor = (color: ColorOption) => {
    setLastPickedColor(color);
  };

  const handleConfirmSelection = () => {
    if (!selectedPart || !selectedMethod || !lastPickedColor) return;
    const partCode = extractCode(selectedPart.code, selectedPart.id);
    const methodCode = extractCode(selectedMethod.code, selectedMethod.id);
    const newSelection: ConfirmedSelection = {
      id: nextSelectionId(),
      partId: selectedPart.id,
      partCode,
      partName: selectedPart.name,
      engName: selectedPart.engName,
      methodId: selectedMethod.id,
      methodCode,
      methodName: selectedMethod.name,
      methodDescription: selectedMethod.description,
      colorHex: lastPickedColor.hex,
      colorName: lastPickedColor.pantoneCode || lastPickedColor.name,
      sourceType: 'finish_l1',
    };
    const updatedSelections = [...confirmedSelections, newSelection];
    setConfirmedSelections(updatedSelections);
    onConfirmed(updatedSelections);
    setSelectedPartId(null);
    setSelectedMethodId(null);
    setLastPickedColor(null);
    setCustomPantoneCode('');
    setCustomPantoneHex('#000000');
    setCustomColorError(null);
    setColorTab('popular');
  };

  const handleResetSelection = () => {
    setSelectedPartId(null);
    setSelectedMethodId(null);
    setLastPickedColor(null);
    setCustomPantoneCode('');
    setCustomPantoneHex('#000000');
    setCustomColorError(null);
    setColorTab('popular');
  };

  const handleRemoveSelection = (selId: string) => {
    const nextSelections = confirmedSelections.filter((s) => s.id !== selId);
    setConfirmedSelections(nextSelections);
    onConfirmed(nextSelections);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-400">{t('customize.finishing.selectDesc')}</p>

      {/* Reset all button */}
      {confirmedSelections.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            onClick={() => {
              setConfirmedSelections([]);
              onConfirmed([]);
              setSelectedPartId(null);
              setSelectedMethodId(null);
              setLastPickedColor(null);
            }}
            className="px-3 py-1.5 text-xs font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />
            {t('customize.resetAll')}
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-0">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            selectedPartId ? 'bg-warm-500 text-white' : 'bg-warm-100 text-warm-500'
          }`}>1</div>
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('customize.selectPart')}</span>
        </div>
        <div className="flex-1 mx-3 h-px bg-ink-200" />
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            selectedMethodId ? 'bg-warm-500 text-white' : 'bg-warm-100 text-warm-500'
          }`}>2</div>
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('customize.selectMethod')}</span>
        </div>
        <div className="flex-1 mx-3 h-px bg-ink-200" />
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            lastPickedColor ? 'bg-emerald-500 text-white' : 'bg-warm-100 text-warm-500'
          }`}>3</div>
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('customize.selectColor')}</span>
        </div>
      </div>

      {/* Grid: 3 columns */}
      <div className="grid grid-cols-3 gap-3">
        {/* Parts */}
        <div className="border border-ink-100 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 bg-cream-50/50">
            <p className="text-[10px] font-bold text-ink-500">
              <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold inline-flex items-center justify-center mr-1">1</span>
              {t('customize.selectPart')}
            </p>
          </div>
          <div className="p-1.5 space-y-0.5 max-h-[200px] overflow-y-auto">
            {partsToUse.map((part: ContainerPart) => {
              const isBlocked = confirmedPartIds.has(part.id);
              if (isBlocked) {
                return (
                  <div
                    key={part.id}
                    className="w-full text-left px-2.5 py-2 rounded-md border-2 border-transparent bg-ink-50/50 opacity-60 cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-ink-400">
                        <span className="text-[9px] font-mono text-ink-300 mr-1">[{part.code}]</span>
                        {part.engName} ({part.name})
                      </span>
                      <span className="text-[10px] text-emerald-500 flex items-center gap-0.5">
                        <i className="ri-check-line" />
                        {t('customize.selected')}
                      </span>
                    </div>
                  </div>
                );
              }
              const isActive = selectedPartId === part.id;
              return (
                <button
                  key={part.id}
                  onClick={() => handleSelectPart(part.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-md transition-all duration-200 cursor-pointer group ${
                    isActive
                      ? 'bg-warm-50 border border-warm-300'
                      : 'border border-transparent hover:bg-cream-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold transition-colors ${isActive ? 'text-warm-600' : 'text-ink-600 group-hover:text-ink-700'}`}>
                      <span className={`text-[9px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{part.code}]</span>
                      {part.engName} ({part.name})
                    </span>
                    <span className={`text-[9px] transition-opacity ${isActive ? 'text-warm-500 opacity-100' : 'text-ink-300 opacity-0 group-hover:opacity-100'}`}>
                      {t('customize.methodsCount', { count: part.methods.length })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Methods */}
        <div className="border border-ink-100 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 bg-cream-50/50">
            <p className="text-[10px] font-bold text-ink-500">
              <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold inline-flex items-center justify-center mr-1">2</span>
              {t('customize.selectMethod')}
              {!selectedPartId && <span className="text-ink-300 text-[9px] font-normal ml-1">{t('customize.selectPartFirst')}</span>}
            </p>
          </div>
          <div className="p-1.5 max-h-[200px] overflow-y-auto">
            {!selectedPartId && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <i className="ri-arrow-left-line text-lg text-ink-200 mb-1.5" />
                <p className="text-[10px] text-ink-300">{t('customize.selectPartHint')}</p>
              </div>
            )}
            {selectedPartId && selectedPart?.methods.map((method) => {
              const isActive = selectedMethodId === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => handleSelectMethod(method.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-md transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-warm-50 border border-warm-300'
                      : 'border border-transparent hover:bg-cream-50'
                  }`}
                >
                  <p className={`text-xs font-bold ${isActive ? 'text-warm-600' : 'text-ink-600'}`}>
                    <span className={`text-[9px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{method.code}]</span>
                    {method.engName} ({method.name.replace(/\s*\([^)]*\)$/, '')})
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Colors */}
        <div className="border border-ink-100 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 bg-cream-50/50">
            <p className="text-[10px] font-bold text-ink-500">
              <span className={`w-5 h-5 rounded-full text-[10px] font-bold inline-flex items-center justify-center mr-1 transition-colors ${
                lastPickedColor ? 'bg-emerald-500 text-white' : 'bg-warm-100 text-warm-500'
              }`}>3</span>
              {t('customize.selectColor')}
              {!selectedMethodId && <span className="text-ink-300 text-[9px] font-normal ml-1">{t('customize.selectMethodFirst')}</span>}
              {selectedMethodId && lastPickedColor && <span className="text-warm-500 text-[9px] font-normal ml-1">{t('customize.confirmHint')}</span>}
            </p>
          </div>
          <div className="p-1.5 flex flex-col min-h-[200px]">
            {!selectedMethodId && (
              <div className="flex flex-col items-center justify-center py-10 text-center flex-1">
                <i className="ri-arrow-left-line text-lg text-ink-200 mb-1.5" />
                <p className="text-[10px] text-ink-300">{t('customize.selectMethodHint')}</p>
              </div>
            )}
            {selectedMethodId && !lastPickedColor && (
              <div className="w-full">
                {/* Tab Bar */}
                <div className="flex items-center bg-ink-50 rounded-lg p-0.5 mb-2">
                  <button
                    onClick={() => setColorTab('popular')}
                    className={`flex-1 py-1 text-[9px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                      colorTab === 'popular' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                    }`}
                  >
                    {t('customize.popularColors')}
                  </button>
                  <button
                    onClick={() => setColorTab('premium')}
                    className={`flex-1 py-1 text-[9px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                      colorTab === 'premium' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                    }`}
                  >
                    Premium
                  </button>
                  <button
                    onClick={() => setColorTab('custom')}
                    className={`flex-1 py-1 text-[9px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                      colorTab === 'custom' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                    }`}
                  >
                    {t('customize.customInput')}
                  </button>
                </div>

                {/* Popular Tab */}
                {colorTab === 'popular' && (
                  <div className="max-h-[180px] overflow-y-auto pr-0.5 space-y-2">
                    {Object.entries(popularByCategory).map(([category, colors]) => (
                      <div key={category}>
                        <h4 className="text-[9px] font-bold text-ink-400 uppercase tracking-wider mb-1 px-1">{category}</h4>
                        <div className="grid grid-cols-2 gap-0.5">
                          {colors.map((color) => (
                            <button
                              key={`fc-pop-${color.pantoneCode}-${color.hex}`}
                              onClick={() => handleSelectColor(color)}
                              className="flex items-center gap-1 p-1 rounded hover:bg-cream-50 transition-colors cursor-pointer text-left border border-transparent hover:border-ink-100"
                            >
                              <div
                                className="w-5 h-5 rounded border border-ink-100 shrink-0"
                                style={{ backgroundColor: color.hex }}
                              />
                              <div className="min-w-0">
                                <p className="text-[9px] font-bold text-ink-700 truncate">{color.pantoneCode}</p>
                                <p className="text-[8px] text-ink-400 truncate">{color.name}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Premium Tab */}
                {colorTab === 'premium' && (
                  <div className="max-h-[180px] overflow-y-auto pr-0.5 space-y-3">
                    {Object.entries(premiumBySubcategory).map(([subKey, colors]) => (
                      <div key={subKey}>
                        <h4 className="text-[9px] font-bold text-ink-400 uppercase tracking-wider mb-1 px-1 flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            subKey === 'metallic' ? 'bg-yellow-400' :
                            subKey === 'pearl' ? 'bg-pink-300' :
                            'bg-gray-400'
                          }`} />
                          {premiumSubLabels[subKey] || subKey}
                        </h4>
                        <div className="grid grid-cols-2 gap-0.5">
                          {colors.map((color) => (
                            <button
                              key={`fc-prem-${color.pantoneCode}-${color.hex}`}
                              onClick={() => handleSelectColor(color)}
                              className="flex items-center gap-1 p-1 rounded hover:bg-cream-50 transition-colors cursor-pointer text-left border border-transparent hover:border-ink-100"
                            >
                              <div
                                className="w-5 h-5 rounded border border-ink-100 shrink-0 relative overflow-hidden"
                                style={{
                                  background: subKey === 'metallic'
                                    ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 40%, ${color.hex} 70%, ${color.hex}cc 100%)`
                                    : subKey === 'pearl'
                                    ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}cc 30%, #ffffff88 50%, ${color.hex}cc 70%, ${color.hex} 100%)`
                                    : color.hex,
                                }}
                              />
                              <div className="min-w-0">
                                <p className="text-[9px] font-bold text-ink-700 truncate">{color.pantoneCode}</p>
                                <p className="text-[8px] text-ink-400 truncate">{color.name}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Custom Tab */}
                {colorTab === 'custom' && (
                  <div className="bg-cream-50/70 rounded-lg p-2.5 border border-ink-100 space-y-2">
                    <p className="text-[9px] text-ink-400">{t('customize.customColorHint')}</p>
                    <div>
                      <label className="text-[9px] font-bold text-ink-600 block mb-0.5">{t('customize.pantoneCode')}</label>
                      <input
                        type="text"
                        value={customPantoneCode}
                        onChange={(e) => { setCustomPantoneCode(e.target.value); setCustomColorError(null); }}
                        placeholder={t('customize.colorPlaceholder')}
                        className="w-full px-2.5 py-1.5 text-xs border border-ink-200 rounded-md focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 bg-white"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        value={customPantoneHex}
                        onChange={(e) => { setCustomPantoneHex(e.target.value); setCustomColorError(null); }}
                        className="w-7 h-7 rounded cursor-pointer border border-ink-200 p-0.5 shrink-0"
                      />
                      <input
                        type="text"
                        value={customPantoneHex}
                        onChange={(e) => { setCustomPantoneHex(e.target.value); setCustomColorError(null); }}
                        placeholder="#FF6B6B"
                        className="flex-1 px-2.5 py-1.5 text-xs border border-ink-200 rounded-md font-mono bg-white"
                      />
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-white border border-ink-100">
                      <div className="w-8 h-8 rounded border border-ink-200 shrink-0" style={{ backgroundColor: customPantoneHex }} />
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-ink-700 truncate">{customPantoneCode || t('customize.noPantone')}</p>
                        <p className="text-[9px] text-ink-400 font-mono">{customPantoneHex}</p>
                      </div>
                    </div>
                    {customColorError && (
                      <p className="text-[9px] text-rose-500 flex items-center gap-1">
                        <i className="ri-error-warning-line text-[10px]" />
                        {customColorError}
                      </p>
                    )}
                    <button
                      onClick={handleConfirmCustomColor}
                      className="w-full py-1.5 text-[10px] font-bold bg-warm-500 text-white rounded-md hover:bg-warm-600 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                    >
                      <i className="ri-check-line text-[10px]" />
                      {t('customize.selectThisColor')}
                    </button>
                  </div>
                )}
              </div>
            )}
            {selectedMethodId && lastPickedColor && (
              <div className="w-full flex flex-col items-center gap-4 py-4">
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3">
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-white shadow-sm shrink-0"
                    style={{ backgroundColor: lastPickedColor.hex }}
                  />
                  <div className="text-left">
                    <p className="text-sm font-bold text-emerald-700">{lastPickedColor.pantoneCode || lastPickedColor.name}</p>
                    <p className="text-xs text-emerald-500 font-mono">{lastPickedColor.name}</p>
                    <p className="text-[10px] text-emerald-400 font-mono">{lastPickedColor.hex}</p>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-emerald-500 text-xs" />
                  </div>
                </div>
                <button
                  onClick={() => { setLastPickedColor(null); setColorTab('popular'); setCustomPantoneCode(''); setCustomPantoneHex('#000000'); setCustomColorError(null); }}
                  className="px-5 py-2.5 bg-white border-2 border-warm-300 text-warm-500 text-sm font-bold rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-palette-line" />
                  {t('customize.changeColor')}
                </button>
                <p className="text-[10px] text-ink-300">{t('customize.changeColorHint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm / Reset buttons - standalone bar below the grid, centered */}
      {selectedMethodId && lastPickedColor && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handleResetSelection}
            className="px-3 py-1.5 text-xs font-bold text-ink-500 border border-ink-200 rounded-lg hover:bg-white hover:border-ink-300 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-refresh-line text-xs" />
            {t('customize.reselect')}
          </button>
          <button
            onClick={handleConfirmSelection}
            className="px-3 py-1.5 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-check-line text-xs" />
            {t('customize.confirm')}
          </button>
        </div>
      )}

      {/* Confirmed Selections Chips */}
      {confirmedSelections.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-cream-50/50 border border-ink-100">
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap shrink-0">
            {t('customize.selectedConditions', { count: confirmedSelections.length })}
          </span>
          <div className="flex flex-wrap gap-2 flex-1 min-w-0">
            {confirmedSelections.map((sel) => (
              <span
                key={sel.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-medium border bg-white border-ink-200 text-ink-600"
              >
                <span
                  className="w-3.5 h-3.5 rounded-full border border-ink-200 shrink-0"
                  style={{ backgroundColor: sel.colorHex }}
                />
                <span className="font-bold">{sel.engName || sel.partName.split('(')[0].trim()}</span>
                <span className="text-ink-400">·</span>
                <span>{sel.methodName.split('(')[0].trim()}</span>
                <span className="text-ink-300">· {sel.colorName}</span>
                <button
                  onClick={() => handleRemoveSelection(sel.id)}
                  className="w-4 h-4 rounded-full bg-ink-100 flex items-center justify-center hover:bg-ink-200 transition-colors cursor-pointer ml-0.5"
                >
                  <i className="ri-close-line text-[8px] text-ink-400" />
                </button>
              </span>
            ))}
          </div>
          <button
            onClick={handleGeneratePreview}
            className="px-3 py-1.5 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1"
          >
            <i className="ri-sparkling-line text-[10px]" />
            {t('customize.finishing.aiGenerate')}
          </button>
        </div>
      )}

      {/* ─── AI Preview Strip ─── */}
      {confirmedSelections.length > 0 && (
        <div className="space-y-3">
          {/* Progress */}
          {isGenerating && jobStatus && (
            <div key="fc-progress" className="bg-gray-200 rounded-xl px-5 py-4 flex items-center gap-3">
              <p className="text-[10px] font-bold text-gray-700 shrink-0 whitespace-nowrap">{t('customize.progressLabel')}</p>
              <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${jobStatus.progress_percent}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-700 font-mono shrink-0">{Math.floor(elapsedDisplay / 60)}:{String(elapsedDisplay % 60).padStart(2, '0')}</span>
              <span className="text-[10px] text-gray-700 font-mono">{jobStatus.progress_percent}%</span>
            </div>
          )}

          {/* Result */}
          {previewResult && !isGenerating && (
            <div key="fc-result" className="bg-blue-50/60 border border-blue-200 rounded-xl px-5 py-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <i className="ri-check-double-line text-blue-500 text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold text-blue-700">{t('customize.aiGeneratedImage')}</p>
                  <p className="text-[10px] text-blue-500 mt-0.5">{t('customize.generatedCheck')}</p>
                </div>
                <button
                  onClick={() => {
                    setPreviewResult(null);
                    setJobStatus(null);
                    setDisplayImageUrl(productImage);
                    setDisplayImageLabel(t('customize.finishing.workingImage'));
                  }}
                  className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center cursor-pointer text-blue-400 hover:text-blue-600 transition-colors"
                  title={t('customize.close')}
                >
                  <i className="ri-close-line" />
                </button>
              </div>

              <div className="flex justify-start items-start gap-[100px]">
                <div className="w-[292px] h-[292px] rounded-lg overflow-hidden border border-blue-200 bg-background-100 flex items-center justify-center shrink-0 relative group">
                  {aiImageError ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center min-w-[200px]">
                      <i className="ri-image-line text-3xl text-ink-300" />
                      <p className="text-xs text-ink-500">{t('customize.imageLoadError')}</p>
                      <button
                        onClick={() => { setAiImageError(false); handleRetryGeneration(); }}
                        className="text-[10px] text-warm-500 underline cursor-pointer"
                      >
                        {t('customize.regenerate')}
                      </button>
                    </div>
                  ) : (
                    <>
                    <img
                      src={previewResult.image_url}
                      alt={t('customize.finishing.resultTitle')}
                      onError={() => setAiImageError(true)}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => navigate('/image-viewer', { state: { imageUrl: previewResult.image_url, title: t('customize.finishing.resultTitle'), imageType: 'finishing' } })}
                        className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                        title={t('customize.viewLarge')}
                      >
                        <i className="ri-zoom-in-line text-sm" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadImage(previewResult.image_url, `${t('customize.finishing.resultTitle')}.png`); }}
                        className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                        title={t('customize.download')}
                      >
                        <i className="ri-download-line text-sm" />
                      </button>
                    </div>
                    </>
                  )}
                </div>
                <div className="w-[300px] text-xs leading-relaxed space-y-3 shrink-0">
                  <p className="text-sm font-extrabold text-ink-700">{t('customize.aiImageInfo')}</p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo1')}
                  </p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo2')}
                  </p>
                  <p className="text-[11px] text-ink-500 leading-relaxed">
                    {t('customize.aiImageInfo3')}
                  </p>
                </div>
              </div>

              <div className="w-[292px] flex items-center justify-center relative">
                <button
                  onClick={() => setShowRetryTooltip(!showRetryTooltip)}
                  className="px-3 py-1.5 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-refresh-line" />
                  {t('customize.aiGenerateAgain')}
                </button>
                {showRetryTooltip && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-20 bg-ink-700 text-white rounded-lg px-3 py-2 shadow-lg max-w-[280px] animate-in fade-in slide-in-from-left-2 duration-150">
                    <p className="text-[9px] leading-relaxed whitespace-normal">
                      i2i에 이전 결과 버퍼 + 새 프롬프트를 넣는 방식은 기술적으로 가능합니다. parent_result_id(또는 result 경로)를 입력으로 받는 refine job을 새로 두고, 그 이미지를 working으로 generateImageWithKling에 넘김
                    </p>
                    <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-r-[6px] border-r-ink-700 border-b-[5px] border-b-transparent" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {generationError && !isGenerating && !previewResult && (
            <div key="fc-error" className="bg-rose-50/60 border border-rose-200 rounded-xl px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-rose-500 text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold text-rose-600">{t('customize.generateFailed')}</p>
                  <p className="text-[10px] text-rose-500 mt-0.5">{generationError}</p>
                  <button
                    onClick={handleRetryGeneration}
                    className="mt-2 px-4 py-1.5 text-[10px] font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-refresh-line" />
                    {t('customize.retry')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}




    </div>
  );
}

// ─── Finishing Only (Part + Method, no color) ───
function FinishingOnlySection({
  productId,
  productImage,
  workingImageId,
  productName,
  categoryL1,
  categoryL2,
  processId,
  onConfirmed,
}: {
  productId: string;
  productImage: string;
  workingImageId?: string | null;
  productName: string;
  categoryL1: string;
  categoryL2: string;
  processId: string;
  onConfirmed: (selections: ConfirmedSelection[]) => void;
}) {
  const { t } = useTranslation('customize');
  const { parts: dbParts } = useContainerParts(categoryL1, categoryL2);

  // Use DB parts if available, fallback to mock
  const partsToUse = useMemo(() => {
    if (dbParts.length > 0) return dbParts;
    return finishingParts;
  }, [dbParts]);

  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [confirmedSelections, setConfirmedSelections] = useState<ConfirmedSelection[]>(() => {
    const saved = getProcessResult(processId);
    return saved?.imageUrl && saved.imageType === 'finishing' ? saved.selections || [] : [];
  });

  // ─── AI Generation State ───
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<FinishingPreviewJob | null>(null);
  const [previewResult, setPreviewResult] = useState<FinishingPreviewResult | null>(() => {
    const saved = getProcessResult(processId);
    if (saved?.imageUrl && saved.imageType === 'finishing') {
      return {
        id: 'restored',
        job_id: '',
        product_id: productId,
        working_image_id: workingImageId || '',
        image_url: saved.imageUrl,
        result_status: 'completed',
        width: 1024,
        height: 1024,
        format: 'png',
      };
    }
    return null;
  });
  const [aiImageError, setAiImageError] = useState(false);
  const [elapsedDisplay, setElapsedDisplay] = useState(0);
  const [showRetryTooltip, setShowRetryTooltip] = useState(false);
  const navigate = useNavigate();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobStartTimeRef = useRef<number>(0);
  const isFirstMountRef = useRef(true);

  const selectedPart = useMemo(() => partsToUse.find((p: ContainerPart) => p.id === selectedPartId) || null, [selectedPartId, partsToUse]);
  const confirmedPartIds = useMemo(() => new Set(confirmedSelections.map((s) => s.partId)), [confirmedSelections]);

  // Reset job flow when confirmed selections change
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    setJobStatus(null);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setIsGenerating(false);
    setElapsedDisplay(0);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, [confirmedSelections]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, []);

  const handleGeneratePreview = async () => {
    if (confirmedSelections.length === 0) return;

    const structuredLines: FinishingLine[] = confirmedSelections.map((s) => ({
      part_code: extractCode(s.partCode, s.partId),
      finish_code: extractCode(s.methodCode, s.methodId),
      color_code: s.colorHex,
    }));

    setIsGenerating(true);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setJobStatus(null);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    try {
      const job = await createPreviewJob({
        product_id: productId,
        working_image_id: workingImageId || productImage,
        structured_lines: structuredLines,
        source: 'readdy_customer_ui',
        request_locale: 'ko-KR',
      });

      setJobStatus(job);
      jobStartTimeRef.current = Date.now();
      setElapsedDisplay(0);

      elapsedTimerRef.current = setInterval(() => {
        setElapsedDisplay(Math.floor((Date.now() - jobStartTimeRef.current) / 1000));
      }, 1000);

      const jobId = job.id;
      pollTimerRef.current = setInterval(async () => {
        try {
          const status = await pollJobStatus(jobId);
          setJobStatus(status);

          if (status.status === 'completed' && status.result_id) {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }

            try {
              const result = await getPreviewResult(status.result_id);
              setPreviewResult(result);
              saveProcessResult(processId, {
                imageUrl: result.image_url,
                title: t('customize.finishing.resultTitle'),
                imageType: 'finishing',
                selections: confirmedSelections,
              });
              setIsGenerating(false);
            } catch (err) {
              setGenerationError(err instanceof Error ? err.message : 'Failed to fetch preview result.');
              setIsGenerating(false);
            }
          }

          if (status.status === 'failed') {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }
            setGenerationError(status.error_message || 'AI preview generation failed.');
            setIsGenerating(false);
          }
        } catch (err) {
          console.error('[FinishingOnly] Poll iteration error:', err);
        }
      }, 2000);
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to create preview job.');
      setIsGenerating(false);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setJobStatus(null);
    setPreviewResult(null);
    handleGeneratePreview();
  };

  const handleCancelGeneration = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setIsGenerating(false);
    setJobStatus(null);
    setPreviewResult(null);
    setGenerationError(null);
    setElapsedDisplay(0);
  };

  const handleSelectPart = (partId: string) => {
    if (selectedPartId === partId) {
      setSelectedPartId(null);
      setSelectedMethodId(null);
    } else {
      setSelectedPartId(partId);
      setSelectedMethodId(null);
    }
  };

  const handleConfirmSelection = () => {
    if (!selectedPartId || !selectedMethodId) return;
    const part = partsToUse.find((p: ContainerPart) => p.id === selectedPartId);
    const method = part?.methods.find((m) => m.id === selectedMethodId);
    if (!part || !method) return;
    const partCode = extractCode(part.code, part.id);
    const methodCode = extractCode(method.code, method.id);
    const newSelection: ConfirmedSelection = {
      id: nextSelectionId(),
      partId: selectedPartId,
      partCode,
      partName: part.name,
      engName: part.engName,
      methodId: selectedMethodId,
      methodCode,
      methodName: method.name,
      methodDescription: method.description,
      colorHex: '#808080',
      colorName: t('customize.keepExistingColor'),
      sourceType: 'finish_l1',
    };
    const updatedSelections = [...confirmedSelections, newSelection];
    setConfirmedSelections(updatedSelections);
    onConfirmed(updatedSelections);
    setSelectedPartId(null);
    setSelectedMethodId(null);
  };

  const handleRemoveSelection = (selId: string) => {
    const nextSelections = confirmedSelections.filter((s) => s.id !== selId);
    setConfirmedSelections(nextSelections);
    onConfirmed(nextSelections);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-400">{t('customize.finishingOnly.selectDesc')}</p>

      {/* Reset all button */}
      {confirmedSelections.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            onClick={() => {
              setConfirmedSelections([]);
              onConfirmed([]);
              setSelectedPartId(null);
              setSelectedMethodId(null);
            }}
            className="px-3 py-1.5 text-xs font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />
            {t('customize.resetAll')}
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-0">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            selectedPartId ? 'bg-warm-500 text-white' : 'bg-warm-100 text-warm-500'
          }`}>1</div>
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('customize.selectPart')}</span>
        </div>
        <div className="flex-1 mx-3 h-px bg-ink-200" />
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            selectedMethodId ? 'bg-emerald-500 text-white' : 'bg-warm-100 text-warm-500'
          }`}>2</div>
          <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('customize.selectMethod')}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Parts */}
        <div className="border border-ink-100 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 bg-cream-50/50">
            <p className="text-[10px] font-bold text-ink-500">
              <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold inline-flex items-center justify-center mr-1">1</span>
              {t('customize.selectPart')}
            </p>
          </div>
          <div className="p-1.5 space-y-0.5 max-h-[200px] overflow-y-auto">
            {partsToUse.map((part: ContainerPart) => {
              const isBlocked = confirmedPartIds.has(part.id);
              if (isBlocked) {
                return (
                  <div
                    key={part.id}
                    className="w-full text-left px-2.5 py-2 rounded-md border-2 border-transparent bg-ink-50/50 opacity-60 cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-ink-400">
                        <span className="text-[9px] font-mono text-ink-300 mr-1">[{part.code}]</span>
                        {part.engName} ({part.name})
                      </span>
                      <span className="text-[10px] text-emerald-500 flex items-center gap-0.5">
                        <i className="ri-check-line" />
                        {t('customize.selected')}
                      </span>
                    </div>
                  </div>
                );
              }
              const isActive = selectedPartId === part.id;
              return (
                <button
                  key={part.id}
                  onClick={() => handleSelectPart(part.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-md transition-all duration-200 cursor-pointer group ${
                    isActive
                      ? 'bg-warm-50 border border-warm-300'
                      : 'border border-transparent hover:bg-cream-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold transition-colors ${isActive ? 'text-warm-600' : 'text-ink-600 group-hover:text-ink-700'}`}>
                      <span className={`text-[9px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{part.code}]</span>
                      {part.engName} ({part.name})
                    </span>
                    <span className={`text-[9px] transition-opacity ${isActive ? 'text-warm-500 opacity-100' : 'text-ink-300 opacity-0 group-hover:opacity-100'}`}>
                      {t('customize.methodsCount', { count: part.methods.length })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Methods */}
        <div className="border border-ink-100 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 bg-cream-50/50">
            <p className="text-[10px] font-bold text-ink-500">
              <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold inline-flex items-center justify-center mr-1">2</span>
              {t('customize.selectMethod')}
              {!selectedPartId && <span className="text-ink-300 text-[9px] font-normal ml-1">{t('customize.selectPartFirst')}</span>}
            </p>
          </div>
          <div className="p-1.5 max-h-[200px] overflow-y-auto">
            {!selectedPartId && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <i className="ri-arrow-left-line text-lg text-ink-200 mb-1.5" />
                <p className="text-[10px] text-ink-300">{t('customize.selectPartHint')}</p>
              </div>
            )}
            {selectedPartId && selectedPart?.methods.map((method) => {
              const isActive = selectedMethodId === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethodId(selectedMethodId === method.id ? null : method.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-md transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-warm-50 border border-warm-300'
                      : 'border border-transparent hover:bg-cream-50'
                  }`}
                >
                  <p className={`text-xs font-bold ${isActive ? 'text-warm-600' : 'text-ink-600'}`}>
                    <span className={`text-[9px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{method.code}]</span>
                    {method.engName} ({method.name.replace(/\s*\([^)]*\)$/, '')})
                  </p>
                </button>
              );
            })}
          </div>
          {/* Confirm button */}
          {selectedPartId && selectedMethodId && (
            <div className="px-3 py-2.5 border-t border-ink-100 bg-cream-50/50 flex items-center justify-end">
              <button
                onClick={handleConfirmSelection}
                className="px-3 py-1.5 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-check-line text-xs" />
                {t('customize.confirm')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmed Selections Chips */}
      {confirmedSelections.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-cream-50/50 border border-ink-100">
          {confirmedSelections.map((sel) => (
            <span
              key={sel.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-medium border bg-white border-ink-200 text-ink-600"
            >
              <span className="font-bold">{sel.engName || sel.partName.split('(')[0].trim()}</span>
              <span className="text-ink-400">·</span>
              <span>{sel.methodName.split('(')[0].trim()}</span>
              <button
                onClick={() => handleRemoveSelection(sel.id)}
                className="w-4 h-4 rounded-full bg-ink-100 flex items-center justify-center hover:bg-ink-200 transition-colors cursor-pointer ml-0.5"
              >
                <i className="ri-close-line text-[8px] text-ink-400" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* ─── AI Preview Strip ─── */}
      {confirmedSelections.length > 0 && (
        <div className="space-y-3">
          {/* Idle: Generate button */}
          {!isGenerating && !previewResult && !generationError && (
            <div key="fo-idle" className="bg-ink-700 rounded-xl px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <i className="ri-robot-2-line text-white text-base" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-extrabold text-white">{t('customize.finishing.aiPreview')}</p>
                    <p className="text-[10px] text-white/50 mt-0.5">
                      {confirmedSelections.map((s) => `${s.engName || s.partName} · ${s.methodName.split('(')[0].trim()}`).join(', ')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <button
                  onClick={handleGeneratePreview}
                  className="px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-white text-ink-700 hover:bg-cream-50"
                >
                  <i className="ri-sparkling-line" />
                  {t('customize.finishing.aiGenerate')}
                </button>
              </div>
            </div>
          )}

          {/* Progress */}
          {isGenerating && jobStatus && (
            <div key="fo-progress" className="bg-gray-200 rounded-xl px-5 py-4 flex items-center gap-3">
              <p className="text-[10px] font-bold text-gray-700 shrink-0 whitespace-nowrap">{t('customize.progressLabel')}</p>
              <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${jobStatus.progress_percent}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-700 font-mono shrink-0">{Math.floor(elapsedDisplay / 60)}:{String(elapsedDisplay % 60).padStart(2, '0')}</span>
              <span className="text-[10px] text-gray-700 font-mono">{jobStatus.progress_percent}%</span>
            </div>
          )}

          {/* Result */}
          {previewResult && !isGenerating && (
            <div key="fo-result" className="bg-blue-50/60 border border-blue-200 rounded-xl px-5 py-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <i className="ri-check-double-line text-blue-500 text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold text-blue-700">{t('customize.finishing.aiPreviewComplete')}</p>
                  <p className="text-[10px] text-blue-500 mt-0.5">{t('customize.generatedCheck')}</p>
                </div>
                <button
                  onClick={() => { setPreviewResult(null); setJobStatus(null); }}
                  className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center cursor-pointer text-blue-400 hover:text-blue-600 transition-colors"
                  title={t('customize.close')}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="flex justify-start">
                <div className="w-[307px] h-[307px] rounded-lg overflow-hidden border border-blue-200 bg-background-100 flex items-center justify-center relative group">
                {aiImageError ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center min-w-[200px]">
                    <i className="ri-image-line text-3xl text-ink-300" />
                    <p className="text-xs text-ink-500">{t('customize.imageLoadError')}</p>
                    <button
                      onClick={() => { setAiImageError(false); handleRetryGeneration(); }}
                      className="text-[10px] text-warm-500 underline cursor-pointer"
                    >
                      {t('customize.regenerate')}
                    </button>
                  </div>
                ) : (
                  <>
                  <img
                    src={previewResult.image_url}
                    alt={t('customize.finishing.resultTitle')}
                    onError={() => setAiImageError(true)}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => navigate('/image-viewer', { state: { imageUrl: previewResult.image_url, title: t('customize.finishing.resultTitle'), imageType: 'finishing' } })}
                      className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                      title={t('customize.viewLarge')}
                    >
                      <i className="ri-zoom-in-line text-sm" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadImage(previewResult.image_url, `${t('customize.finishing.resultTitle')}.png`); }}
                      className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                      title={t('customize.download')}
                    >
                      <i className="ri-download-line text-sm" />
                    </button>
                  </div>
                  </>
                )}
                </div>
              </div>
              <div className="w-[307px] flex items-center justify-center relative">
                <button
                  onClick={() => setShowRetryTooltip(!showRetryTooltip)}
                  className="px-3 py-1.5 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-refresh-line" />
                  {t('customize.aiGenerateAgain')}
                </button>
                {showRetryTooltip && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-20 bg-ink-700 text-white rounded-lg px-3 py-2 shadow-lg max-w-[280px] animate-in fade-in slide-in-from-left-2 duration-150">
                    <p className="text-[9px] leading-relaxed whitespace-normal">
                      i2i에 이전 결과 버퍼 + 새 프롬프트를 넣는 방식은 기술적으로 가능합니다. parent_result_id(또는 result 경로)를 입력으로 받는 refine job을 새로 두고, 그 이미지를 working으로 generateImageWithKling에 넘김
                    </p>
                    <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-r-[6px] border-r-ink-700 border-b-[5px] border-b-transparent" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {generationError && !isGenerating && !previewResult && (
            <div key="fo-error" className="bg-rose-50/60 border border-rose-200 rounded-xl px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-rose-500 text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold text-rose-600">{t('customize.generateFailed')}</p>
                  <p className="text-[10px] text-rose-500 mt-0.5">{generationError}</p>
                  <button
                    onClick={handleRetryGeneration}
                    className="mt-2 px-4 py-1.5 text-[10px] font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-refresh-line" />
                    {t('customize.retry')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}




    </div>
  );
}

// ??? Printing Add ???
interface PrintMethod {
  code: string;
  name: string;
  note: string;
}

function PrintingAddSection({
  productImage,
  productName,
  productId,
  workingImageId,
  processId,
  onConfirmed,
}: {
  productImage: string;
  productName: string;
  productId: string;
  workingImageId?: string | null;
  processId: string;
  onConfirmed: (selections: ConfirmedSelection[]) => void;
}) {
  const { t } = useTranslation('customize');
  const positionLabel = (id: string | null) => {
    const p = printingPositions.find((x) => x.id === id);
    return p ? (p.labelKey ? t(p.labelKey) : p.label) : '';
  };
  const [selectedPrint, setSelectedPrint] = useState<PrintMethod | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);

  const [printMethods, setPrintMethods] = useState<PrintMethod[]>([]);
  const [printMethodsLoading, setPrintMethodsLoading] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoTransferOnly, setPhotoTransferOnly] = useState(false);

  // {t('customize.print.photoTransfer')} 체크박스 선택 시 position='01', print_code='99' 자동 설정
  useEffect(() => {
    if (photoTransferOnly) {
      setSelectedPosition('01');
      const photoMethod = printMethods.find((m) => String(m.code) === '99');
      if (photoMethod) {
        setSelectedPrint(photoMethod);
      }
    } else {
      setSelectedPosition(null);
      setSelectedPrint(null);
    }
  }, [photoTransferOnly, printMethods]);

  const filteredPrintMethods = useMemo(() => {
    if (!photoTransferOnly) return printMethods;
    return printMethods.filter((m) => m.name.includes('사진전사'));
  }, [printMethods, photoTransferOnly]);

  const [printDropdownOpen, setPrintDropdownOpen] = useState(false);
  const printDropdownRef = useRef<HTMLDivElement>(null);
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const positionDropdownRef = useRef<HTMLDivElement>(null);
  // ─── CAP 상단면 경고 팝업 ───
  const [showCapTopWarning, setShowCapTopWarning] = useState(false);

  // ─── AI 인쇄 미리보기 생성 상태 ───
  const [uploadedArtworkId, setUploadedArtworkId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatusText, setJobStatusText] = useState('');
  const [jobProgressPercent, setJobProgressPercent] = useState(0);
  const [previewResult, setPreviewResult] = useState<{ result_id: string; image_url: string; width: number; height: number } | null>(() => {
    const saved = getProcessResult(processId);
    if (saved?.imageUrl && saved.imageType === 'printing') {
      return { result_id: 'restored', image_url: saved.imageUrl, width: 0, height: 0 };
    }
    return null;
  });
  const [aiImageError, setAiImageError] = useState(false);
  const [elapsedDisplay, setElapsedDisplay] = useState(0);
  const [showRetryTooltip, setShowRetryTooltip] = useState(false);
  const navigate = useNavigate();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobStartTimeRef = useRef<number>(0);
  const isFirstMountRef = useRef(true);

  // ─── Fetch print methods from master_codes ───
  useEffect(() => {
    async function fetchPrintMethods() {
      try {
        if (!supabase) throw new Error('Supabase not available');
        const { data, error } = await supabase
          .from('master_codes')
          .select('code, name, note')
          .eq('code_type', 'print')
          .eq('is_active', true)
          .order('sort_order');
        if (!error && data) {
          setPrintMethods(data as PrintMethod[]);
        } else {
          throw new Error(error?.message || 'Failed to fetch');
        }
      } catch (err) {
        // No mock fallback — keep empty list with error logged
        console.error('[PrintingAdd] Failed to fetch print methods from DB:', err);
        setPrintMethods([]);
      } finally {
        setPrintMethodsLoading(false);
      }
    }
    fetchPrintMethods();
  }, []);

  // ─── Cleanup preview URL on unmount ───
  useEffect(() => {
    return () => {
      if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    };
  }, [uploadedPreviewUrl]);

  // ─── AI generation cleanup on selections change ───
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    setJobId(null);
    setJobStatusText('');
    setJobProgressPercent(0);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setIsGenerating(false);
    setElapsedDisplay(0);
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
  }, [selectedPrint, selectedPosition, uploadedFile]);

  // ─── Timer cleanup on unmount ───
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ─── 클라이언트 검증: 업로드 전 포맷·용량·해상도·비율 검사 ───
    const validation = await validateImageFile(file);
    if (!validation.ok) {
      setUploadError(validation.message || t('customize.print.invalidFile'));
      // 입력 초기화 (같은 파일 재선택 가능하도록)
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // 검증 통과 → 미리보기 설정
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedFile(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    setUploadError(null);
    setIsUploading(false);
  };

  const handleRemoveFile = () => {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedFile(null);
    setUploadedPreviewUrl(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // ─── Upload artwork to Supabase Storage → public URL (no product_images insert) ───
  const uploadArtworkToSupabase = async (file: File): Promise<{ imageId?: string; imageUrl: string }> => {
    if (!supabase) throw new Error('Supabase not available');
    const ext = file.name.split('.').pop() || 'png';
    const fileName = `artworks/${crypto.randomUUID()}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from('public')
      .upload(fileName, file, { contentType: file.type, upsert: false });
    if (uploadErr) throw new Error(t('customize.print.uploadFailed', { message: uploadErr.message }));

    const { data: urlData } = supabase.storage.from('public').getPublicUrl(fileName);
    return { imageUrl: urlData.publicUrl };
  };

  // ─── AI 인쇄 미리보기 생성 ───
  const handleGeneratePreview = async () => {
    // {t('customize.print.photoTransfer')} 모드: position/print는 무조건 '01'/'99'로 강제
    const effectivePosition = photoTransferOnly ? '01' : selectedPosition;
    const effectivePrintCode = photoTransferOnly ? '99' : (selectedPrint ? String(selectedPrint.code) : '');

    if ((!photoTransferOnly && (!selectedPrint || !selectedPosition)) || !uploadedFile) return;
    if (!workingImageId) {
      setGenerationError(t('customize.print.noWorkingImage'));
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setJobId(null);
    setJobStatusText('');
    setJobProgressPercent(0);
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }

    try {
      // 1. Upload artwork if not already uploaded
      let artworkUrl = uploadedPreviewUrl as string | undefined;
      const alreadyUploaded = uploadedArtworkId !== null;
      if (!alreadyUploaded) {
        setJobStatusText(t('customize.print.uploadingArtwork'));
        const uploadResult = await uploadArtworkToSupabase(uploadedFile);
        artworkUrl = uploadResult.imageUrl;
        setUploadedArtworkId('uploaded'); // mark as uploaded
      }

      // 2. Create printing preview job directly via Supabase (no ngrok)
      setJobStatusText(t('customize.print.creatingJob'));

      const job = await createPrintingPreviewJob({
        product_id: productId,
        working_image_id: workingImageId,
        position_code: String(effectivePosition),
        print_code: String(effectivePrintCode),
        artwork_image_url: artworkUrl,
        source: 'readdy_customer_ui',
        request_locale: 'ko-KR',
      });

      setJobId(job.id);
      setJobStatusText(job.progress_message);
      setJobProgressPercent(job.progress_percent);
      jobStartTimeRef.current = Date.now();
      setElapsedDisplay(0);

      elapsedTimerRef.current = setInterval(() => {
        setElapsedDisplay(Math.floor((Date.now() - jobStartTimeRef.current) / 1000));
      }, 1000);

      // 3. Poll until completed or failed (2s interval)
      const currentJobId = job.id;
      pollTimerRef.current = setInterval(async () => {
        try {
          const status = await pollPrintingJobStatus(currentJobId);
          setJobStatusText(status.progress_message);
          setJobProgressPercent(status.progress_percent);

          if (status.status === 'completed' && status.result_image_url) {
            if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
            if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }

            setPreviewResult({
              result_id: status.result_id || '',
              image_url: status.result_image_url,
              width: 0,
              height: 0,
            });
            saveProcessResult(processId, {
              imageUrl: status.result_image_url,
              title: t('customize.printing.resultTitle'),
              imageType: 'printing',
              selections: [],
            });
            setIsGenerating(false);
          } else if (status.status === 'failed') {
            if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
            if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
            setGenerationError(status.error_message || t('customize.print.generateFailed'));
            setIsGenerating(false);
          }
          // else: still processing — keep polling
        } catch (err) {
          console.error('[PrintingAdd] Poll error:', err);
          if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
          if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
          setGenerationError(err instanceof Error ? err.message : t('customize.print.statusError'));
          setIsGenerating(false);
        }
      }, 2000);
    } catch (err) {
      console.error('[PrintingAdd] Generate error:', err);
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
      setGenerationError(err instanceof Error ? err.message : t('customize.print.unknownError'));
      setIsGenerating(false);
    }
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setJobId(null);
    setJobStatusText('');
    setJobProgressPercent(0);
    setPreviewResult(null);
    handleGeneratePreview();
  };

  const handleCancelGeneration = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setIsGenerating(false);
    setJobId(null);
    setJobStatusText('');
    setJobProgressPercent(0);
    setPreviewResult(null);
    setGenerationError(null);
    setElapsedDisplay(0);
  };

  const handleConfirm = () => {
    if (!selectedPrint) return;
    const posLabel = positionLabel(selectedPosition) || t('customize.print.positionUnset');
    onConfirmed([{
      id: nextSelectionId(),
      partId: selectedPosition || 'part-body',
      partCode: '03',
      partName: posLabel,
      methodId: `print-${String(selectedPrint.code)}`,
      methodCode: String(selectedPrint.code),
      methodName: `${String(selectedPrint.code)} ${selectedPrint.name}`,
      colorHex: '#333333',
      colorName: uploadedFile?.name || t('customize.print.noDesignFile'),
      sourceType: 'print',
    }]);
  };

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* ─── Row 1: Product Image + Controls (side by side) ─── */}
      <div className="flex flex-col lg:flex-row items-start gap-6">
        <div className="shrink-0 w-[320px] h-[320px] bg-background-100 rounded-xl border border-ink-100 flex items-center justify-center p-4 overflow-hidden">
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Right side: Print Position → Print Method */}
        <div className="flex-1 min-w-0 space-y-4">
          <p className="text-sm font-bold text-ink-700">{productName}</p>

          {/* ─── Step 1: Print Position (top) — dropdown ─── */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-5 h-5 rounded-md bg-warm-100 flex items-center justify-center shrink-0">
                <i className="ri-map-pin-line text-warm-500 text-[10px]" />
              </div>
              <p className="text-xs font-bold text-ink-600">{t('customize.print.position')}</p>
              {selectedPosition && (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-[9px] font-bold">
                  {t('customize.selected')}
                </span>
              )}
            </div>

            <div ref={positionDropdownRef}>
              {/* Dropdown trigger */}
              <button
                type="button"
                onClick={() => { if (!photoTransferOnly) setPositionDropdownOpen(!positionDropdownOpen); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                  photoTransferOnly
                    ? 'bg-ink-50 border-ink-200 cursor-default'
                    : positionDropdownOpen
                      ? 'border-warm-400 ring-1 ring-warm-200 rounded-b-none bg-white'
                      : selectedPosition
                        ? 'border-emerald-300 bg-white'
                        : 'border-ink-200 hover:border-warm-200 bg-white'
                }`}
              >
                {selectedPosition ? (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="w-5 h-5 rounded bg-warm-500 text-white text-[9px] font-mono font-bold flex items-center justify-center shrink-0">
                      {selectedPosition}
                    </span>
                    <span className="text-xs font-bold text-ink-700 truncate">
                      {positionLabel(selectedPosition) || selectedPosition}
                    </span>
                    {photoTransferOnly && (
                      <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-600 text-[8px] font-bold shrink-0">{t('customize.print.autoSet')}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-ink-400">{t('customize.print.positionPlaceholder')}</span>
                )}
                <div className="flex items-center gap-0.5 shrink-0">
                  {selectedPosition && !photoTransferOnly && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPosition(null);
                      }}
                      className="w-4 h-4 rounded-full hover:bg-ink-100 flex items-center justify-center cursor-pointer text-ink-400 hover:text-ink-600 transition-colors"
                    >
                      <i className="ri-close-line text-[8px]" />
                    </button>
                  )}
                  {photoTransferOnly ? (
                    <i className="ri-lock-line text-ink-400 text-[10px]" />
                  ) : (
                    <i className={`ri-arrow-down-s-line text-ink-400 text-xs transition-transform duration-200 ${positionDropdownOpen ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </button>

              {/* Inline dropdown list */}
              {positionDropdownOpen && !photoTransferOnly && (
                <div className="border-2 border-t-0 border-warm-400 rounded-b-lg bg-white overflow-hidden max-h-[200px] overflow-y-auto">
                  {printingPositions.map((pos) => {
                    const isActive = selectedPosition === String(pos.id);
                    return (
                      <button
                        key={pos.id}
                        type="button"
                        onClick={() => {
                          if (isActive) {
                            setSelectedPosition(null);
                          } else {
                            setSelectedPosition(pos.id);
                            if (pos.id === '04') {
                              setShowCapTopWarning(true);
                            }
                          }
                          setPositionDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 text-left transition-colors cursor-pointer border-b border-ink-50 last:border-b-0 ${
                          isActive
                            ? 'bg-warm-50'
                            : 'hover:bg-cream-50/60'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded flex items-center justify-center shrink-0 text-[9px] font-mono font-bold ${
                            isActive
                              ? 'bg-warm-500 text-white'
                              : 'bg-ink-100 text-ink-500'
                          }`}
                        >
                          {pos.id}
                        </span>
                        <span
                          className={`text-xs font-bold flex-1 truncate ${
                            isActive ? 'text-warm-600' : 'text-ink-700'
                          }`}
                        >
                          {pos.labelKey ? t(pos.labelKey) : pos.label}
                        </span>
                        {isActive && (
                          <i className="ri-check-line text-warm-500 text-xs shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ─── Step 2: Print Method Dropdown (middle) ─── */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-5 h-5 rounded-md bg-warm-100 flex items-center justify-center shrink-0">
                <i className="ri-printer-line text-warm-500 text-[10px]" />
              </div>
              <p className="text-xs font-bold text-ink-600">{t('customize.print.method')}</p>
              <label className="flex items-center gap-1 cursor-pointer ml-2">
                <input
                  type="checkbox"
                  checked={photoTransferOnly}
                  onChange={(e) => setPhotoTransferOnly(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-ink-300 text-warm-500 focus:ring-warm-400 cursor-pointer"
                />
                <span className="text-[10px] font-bold text-ink-500 whitespace-nowrap">{t('customize.print.photoTransfer')}</span>
              </label>
              {selectedPrint && (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-[9px] font-bold">
                  {t('customize.selected')}
                </span>
              )}
            </div>

            {printMethodsLoading ? (
              <div className="h-[42px] bg-ink-50/50 rounded-lg animate-pulse" />
            ) : (
              <div ref={printDropdownRef}>
                {/* Dropdown trigger */}
                <button
                  type="button"
                  onClick={() => { if (!photoTransferOnly) setPrintDropdownOpen(!printDropdownOpen); }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                    photoTransferOnly
                      ? 'bg-ink-50 border-ink-200 cursor-default'
                      : printDropdownOpen
                        ? 'border-warm-400 ring-1 ring-warm-200 rounded-b-none bg-white'
                        : selectedPrint
                          ? 'border-emerald-300 bg-white'
                          : 'border-ink-200 hover:border-warm-200 bg-white'
                  }`}
                >
                  {selectedPrint ? (
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="w-5 h-5 rounded bg-warm-500 text-white text-[9px] font-mono font-bold flex items-center justify-center shrink-0">
                        {selectedPrint.code}
                      </span>
                      <span className="text-xs font-bold text-ink-700 truncate">{selectedPrint.name}</span>
                      {photoTransferOnly && (
                        <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-600 text-[8px] font-bold shrink-0">{t('customize.print.autoSet')}</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-ink-400">{t('customize.print.methodPlaceholder')}</span>
                  )}
                  <div className="flex items-center gap-0.5 shrink-0">
                    {selectedPrint && !photoTransferOnly && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPrint(null);
                        }}
                        className="w-4 h-4 rounded-full hover:bg-ink-100 flex items-center justify-center cursor-pointer text-ink-400 hover:text-ink-600 transition-colors"
                      >
                        <i className="ri-close-line text-[8px]" />
                      </button>
                    )}
                    {photoTransferOnly ? (
                      <i className="ri-lock-line text-ink-400 text-[10px]" />
                    ) : (
                      <i className={`ri-arrow-down-s-line text-ink-400 text-xs transition-transform duration-200 ${printDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </button>

                {/* Inline dropdown list — pushes content below, no floating panel */}
                {printDropdownOpen && !photoTransferOnly && (
                  <div className="border-2 border-t-0 border-warm-400 rounded-b-lg bg-white overflow-hidden max-h-[200px] overflow-y-auto">
                    {filteredPrintMethods.map((method) => {
                      const isActive = selectedPrint ? String(selectedPrint.code) === String(method.code) : false;
                      return (
                        <button
                          key={method.code}
                          type="button"
                          onClick={() => {
                            setSelectedPrint(isActive ? null : method);
                            setPrintDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-2.5 py-2 text-left transition-colors cursor-pointer border-b border-ink-50 last:border-b-0 ${
                            isActive
                              ? 'bg-warm-50'
                              : 'hover:bg-cream-50/60'
                          }`}
                        >
                          <span
                            className={`w-5 h-5 rounded flex items-center justify-center shrink-0 text-[9px] font-mono font-bold ${
                              isActive
                                ? 'bg-warm-500 text-white'
                                : 'bg-ink-100 text-ink-500'
                            }`}
                          >
                            {method.code}
                          </span>
                          <span
                            className={`text-xs font-bold flex-1 truncate ${
                              isActive ? 'text-warm-600' : 'text-ink-700'
                            }`}
                          >
                            {method.name}
                          </span>
                          {isActive && (
                            <i className="ri-check-line text-warm-500 text-xs shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── File preview inline ─── */}
          <div className="flex items-start gap-3">
            <button
              onClick={handleUploadClick}
              className="w-[88px] h-[66px] bg-white border-2 border-dashed border-ink-200 rounded-lg flex flex-col items-center justify-center gap-0.5 overflow-hidden shrink-0 relative group cursor-pointer hover:border-warm-300 hover:bg-cream-50/50 transition-colors"
              title={t('customize.print.uploadDesign')}
            >
              {uploadedPreviewUrl ? (
                <>
                  <img
                    src={uploadedPreviewUrl}
                    alt={t('customize.print.uploadedFileAlt')}
                    className="w-full h-full object-contain p-1"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10"
                    title={t('customize.print.removeFile')}
                  >
                    <i className="ri-close-line text-[8px]" />
                  </button>
                </>
              ) : (
                <>
                  <i className="ri-add-line text-ink-400 text-lg" />
                  <span className="text-[8px] text-ink-500 font-bold">{t('customize.print.upload')}</span>
                </>
              )}
            </button>

            <div className="flex-1 min-w-0 space-y-1 pt-0.5">
              <p className="text-[10px] text-ink-400 leading-relaxed">{t('customize.print.allowedFormat')}</p>
              <p className="text-[10px] text-ink-400 leading-relaxed">{t('customize.print.maxSize')}</p>
              <p className="text-[10px] text-ink-400 leading-relaxed">{t('customize.print.ratio')}</p>
              {uploadedFile && (
                <p className="text-[10px] text-emerald-600 flex items-center gap-1">
                  <i className="ri-check-line text-[10px]" />
                  {uploadedFile.name}
                  <span className="text-ink-400 font-normal">({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
                </p>
              )}
              {uploadError && (
                <p className="text-[10px] text-rose-500 flex items-start gap-1 leading-relaxed">
                  <i className="ri-error-warning-line text-[10px] shrink-0 mt-0.5" />
                  <span>{uploadError}</span>
                </p>
              )}
            </div>
          </div>

          {/* ─── {t('customize.confirm')} ─── */}
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={handleConfirm}
              disabled={photoTransferOnly ? !uploadedFile : (!selectedPrint || !selectedPosition || !uploadedFile)}
              className={`px-6 py-2.5 text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                (photoTransferOnly ? uploadedFile : (selectedPrint && selectedPosition && uploadedFile))
                  ? 'bg-warm-500 text-white hover:bg-warm-600'
                  : 'bg-ink-100 text-ink-400 cursor-not-allowed'
              }`}
            >
              <i className="ri-check-line" />
              {t('customize.confirm')}
            </button>
          </div>

          {/* ─── AI 생성 버튼 ─── */}
          {!isGenerating && !previewResult && !generationError && (
            <div key="pa-idle" className="flex justify-center">
              <button
                onClick={handleGeneratePreview}
                disabled={photoTransferOnly ? !uploadedFile : (!selectedPrint || !selectedPosition || !uploadedFile)}
                className={`px-5 py-2.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  (photoTransferOnly ? uploadedFile : (selectedPrint && selectedPosition && uploadedFile))
                    ? 'bg-warm-500 text-white hover:bg-warm-600'
                    : 'bg-ink-100 text-ink-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-sparkling-line" />
                {t('customize.print.aiPreview')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── AI Progress (below working image) ─── */}
      {isGenerating && jobId && (
        <div key="pa-progress" className="bg-gray-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <p className="text-[10px] font-bold text-gray-700 shrink-0 whitespace-nowrap">{t('customize.progressLabel')}</p>
          <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
            <div
              className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${jobProgressPercent}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-700 font-mono shrink-0">{Math.floor(elapsedDisplay / 60)}:{String(elapsedDisplay % 60).padStart(2, '0')}</span>
          <span className="text-[10px] text-gray-700 font-mono">{jobProgressPercent}%</span>
        </div>
      )}

      {/* ─── AI Error (below working image) ─── */}
      {generationError && !isGenerating && !previewResult && (
        <div key="pa-error" className="bg-rose-50/60 border border-rose-200 rounded-xl px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-rose-500 text-base" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-extrabold text-rose-600">{t('customize.generateFailed')}</p>
              <p className="text-[10px] text-rose-500 mt-0.5">{generationError}</p>
              <button
                onClick={handleRetryGeneration}
                className="mt-2 px-4 py-1.5 text-[10px] font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-refresh-line" />
                {t('customize.retry')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── AI Result (below working image, left-aligned) ─── */}
      {previewResult && !isGenerating && (
        <div key="pa-result" className="bg-blue-50/60 border border-blue-200 rounded-xl px-5 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <i className="ri-check-double-line text-blue-500 text-base" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-extrabold text-blue-700">{t('customize.aiGeneratedImage')}</p>
              <p className="text-[10px] text-blue-500 mt-0.5">{t('customize.generatedCheck')}</p>
            </div>
            <button
              onClick={() => { setPreviewResult(null); setJobId(null); }}
              className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center cursor-pointer text-blue-400 hover:text-blue-600 transition-colors"
              title={t('customize.close')}
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="flex justify-start items-start gap-[100px]">
            <div className="w-[292px] h-[292px] rounded-lg overflow-hidden border border-blue-200 bg-background-100 flex items-center justify-center shrink-0 relative group">
              {aiImageError ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center min-w-[200px]">
                  <i className="ri-image-line text-3xl text-ink-300" />
                  <p className="text-xs text-ink-500">{t('customize.imageLoadError')}</p>
                  <button
                    onClick={() => { setAiImageError(false); handleRetryGeneration(); }}
                    className="text-[10px] text-warm-500 underline cursor-pointer"
                  >
                    {t('customize.regenerate')}
                  </button>
                </div>
              ) : (
                <>
                  <img
                    src={previewResult.image_url}
                    alt={t('customize.printing.resultTitle')}
                    onError={() => setAiImageError(true)}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => navigate('/image-viewer', { state: { imageUrl: previewResult.image_url, title: t('customize.printing.resultTitle'), imageType: 'printing' } })}
                      className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                      title={t('customize.viewLarge')}
                    >
                      <i className="ri-zoom-in-line text-sm" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadImage(previewResult.image_url, `${t('customize.printing.resultTitle')}.png`); }}
                      className="w-7 h-7 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center cursor-pointer transition-colors"
                      title={t('customize.download')}
                    >
                      <i className="ri-download-line text-sm" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="w-[300px] text-xs leading-relaxed space-y-3 shrink-0">
              <p className="text-sm font-extrabold text-ink-700">{t('customize.aiImageInfo')}</p>
              <p className="text-[11px] text-ink-500 leading-relaxed">
                {t('customize.aiImageInfo1')}
              </p>
              <p className="text-[11px] text-ink-500 leading-relaxed">
                {t('customize.aiImageInfo2')}
              </p>
              <p className="text-[11px] text-ink-500 leading-relaxed">
                {t('customize.aiImageInfo3')}
              </p>
            </div>
          </div>

          <div className="w-[292px] flex items-center justify-center relative">
            <button
              onClick={() => setShowRetryTooltip(!showRetryTooltip)}
              className="px-3 py-1.5 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-refresh-line" />
              {t('customize.aiGenerateAgain')}
            </button>
            {showRetryTooltip && (
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-20 bg-ink-700 text-white rounded-lg px-3 py-2 shadow-lg max-w-[280px] animate-in fade-in slide-in-from-left-2 duration-150">
                <p className="text-[9px] leading-relaxed whitespace-normal">
                  i2i에 이전 결과 버퍼 + 새 프롬프트를 넣는 방식은 기술적으로 가능합니다. parent_result_id(또는 result 경로)를 입력으로 받는 refine job을 새로 두고, 그 이미지를 working으로 generateImageWithKling에 넘김
                </p>
                <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-r-[6px] border-r-ink-700 border-b-[5px] border-b-transparent" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── CAP 상단면 경고 팝업 ─── */}
      {showCapTopWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 배경 오버레이 */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCapTopWarning(false)}
          />
          {/* 팝업 카드 */}
          <div className="relative z-10 mx-4 w-full max-w-[420px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* 상단 경고 바 */}
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-amber-500 text-xl" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-amber-700">{t('customize.print.capTopTitle')}</p>
                <p className="text-[10px] text-amber-500 mt-0.5">{t('customize.print.capTopSubtitle')}</p>
              </div>
            </div>
            {/* 본문 */}
            <div className="px-6 py-5">
              <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4">
                <p className="text-sm font-bold text-ink-700 leading-relaxed">
                  {t('customize.print.capTopBody')}
                </p>
                <div className="mt-3 flex items-start gap-2">
                  <i className="ri-information-line text-amber-400 text-sm shrink-0 mt-0.5" />
                  <p className="text-[11px] text-ink-500 leading-relaxed">
{t('customize.print.capTopHint')}
                  </p>
                </div>
              </div>
            </div>
            {/* 하단 버튼 */}
            <div className="px-6 pb-5 pt-2">
              <button
                onClick={() => setShowCapTopWarning(false)}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-warm-500 text-white hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-check-line" />
                {t('customize.print.understood')}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

// ─── Main ProcessSection ───
export default function ProcessSection({
  processId,
  processType,
  title,
  labelKey,
  index,
  productId,
  productImage,
  workingImageId,
  productName,
  categoryL1,
  categoryL2,
  outerDiameterMm,
  innerDiameterMm,
  heightMm,
  onRemove,
  onConfirmed,
  collapseOnConfirm = true,
}: ProcessSectionProps) {
  const { t } = useTranslation('customize');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedData, setConfirmedData] = useState<ConfirmedSelection[]>([]);

  const handleInternalConfirm = useCallback((selections: ConfirmedSelection[]) => {
    setConfirmedData(selections);
    if (collapseOnConfirm) setIsConfirmed(true);
    onConfirmed(processId, selections);
  }, [processId, onConfirmed, collapseOnConfirm]);

  const handleEdit = useCallback(() => {
    setIsConfirmed(false);
    setConfirmedData([]);
  }, []);

  const icon = PROCESS_TYPE_ICONS[processType];

  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-5 py-3 border-b border-ink-100 bg-cream-50/30 flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-6 h-6 rounded-md bg-ink-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
            {index + 1}
          </span>
          <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0">
            <i className={`${icon} text-warm-500 text-sm`} />
          </span>
          <h3 className="text-sm font-bold text-ink-700 truncate">{labelKey ? t(labelKey) : title}</h3>
          {isConfirmed && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-[10px] font-bold flex items-center gap-1 shrink-0">
              <i className="ri-check-line text-[10px]" />
              {t('customize.done')}
            </span>
          )}
        </div>
        <button
          onClick={() => onRemove(processId)}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-rose-50 text-ink-400 hover:text-rose-500 transition-colors cursor-pointer shrink-0"
          title={t('customize.removeProcess')}
        >
          <i className="ri-close-line" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 md:p-5">
        {isConfirmed ? (
          <div className="space-y-3">
            <p className="text-xs text-ink-400">{t('customize.confirmedMessage')}</p>
            <div className="space-y-2">
              {confirmedData.map((sel) => (
                <div key={sel.id} className="flex items-center gap-2.5 px-3 py-2 bg-cream-50/70 rounded-lg border border-ink-100">
                  <span
                    className="w-5 h-5 rounded-full border border-ink-200 shrink-0"
                    style={{ backgroundColor: sel.colorHex }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-ink-700">
                      {sel.engName ? `${sel.engName} (${sel.partName})` : sel.partName}
                      {' · '}
                      {sel.methodDescription ? `${sel.methodDescription} (${sel.methodName})` : sel.methodName}
                    </p>
                    <p className="text-[10px] text-ink-400">{sel.colorName}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-xs font-bold text-warm-600 border-2 border-warm-300 rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-edit-line" />
              {t('customize.edit')}
            </button>
          </div>
        ) : (
          <div>
            {processType === 'parts_replace' && <PartsReplaceSection productId={productId} productImage={productImage} productName={productName} outerDiameterMm={outerDiameterMm} innerDiameterMm={innerDiameterMm} heightMm={heightMm} processId={processId} onConfirmed={handleInternalConfirm} />}

            {processType === 'color_only' && <ColorOnlySection onConfirmed={handleInternalConfirm} />}
            {(processType === 'finishing_color' || processType === 'finishing_color_completed') && (
              <FinishingColorSection productId={productId} productImage={productImage} workingImageId={workingImageId} productName={productName} categoryL1={categoryL1} categoryL2={categoryL2} processId={processId} onConfirmed={handleInternalConfirm} />
            )}
            {processType === 'finishing_only' && <FinishingOnlySection productId={productId} productImage={productImage} workingImageId={workingImageId} productName={productName} categoryL1={categoryL1} categoryL2={categoryL2} processId={processId} onConfirmed={handleInternalConfirm} />}
            {processType === 'printing_add' && <PrintingAddSection productImage={productImage} productName={productName} productId={productId} workingImageId={workingImageId} processId={processId} onConfirmed={handleInternalConfirm} />}
          </div>
        )}
      </div>
    </div>
  );
}